// Settings — Agents, Members, Integrations panes
// (continues settings.jsx — kept separate so settings.jsx doesn't blow past 1k lines)

// ============ AGENTS ============
// Wired to /api/agents/catalog — the same source as the Agents top-level view.
// Lets admins toggle each agent enabled/hidden via the catalog override endpoint.
// Real usage stats come from /api/agent-workspaces (workspaces per lead_agent_key).

const AGENT_TONES = ['blue', 'teal', 'violet', 'amber', 'rose', 'green', 'slate'];
const toneFor = (key, i) => AGENT_TONES[i % AGENT_TONES.length];

const AgentsSettings = () => {
  const [catalog,    setCatalog]    = React.useState(null);
  const [workspaces, setWorkspaces] = React.useState([]);
  const [selectedKey, setSelectedKey] = React.useState(null);

  const reload = React.useCallback(async () => {
    const [c, ws] = await Promise.all([
      window.AtlasApi.agentCatalog({ includeHidden: true }).catch(() => []),
      window.AtlasApi.agentWorkspaces().catch(() => []),
    ]);
    setCatalog(Array.isArray(c) ? c : []);
    setWorkspaces(Array.isArray(ws) ? ws : []);
    if (!selectedKey && Array.isArray(c) && c.length) setSelectedKey(c[0].key);
  }, [selectedKey]);

  React.useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // NB: hooks must run before any early return — keep useMemo above the
  // `catalog === null` loading guard or React throws #310 (hook count
  // changes between the loading render and the loaded render).
  const wsByAgent = React.useMemo(() => {
    const m = {};
    workspaces.forEach((w) => {
      if (!w.lead_agent_key) return;
      m[w.lead_agent_key] = (m[w.lead_agent_key] || 0) + 1;
    });
    return m;
  }, [workspaces]);

  if (catalog === null) {
    return <SettingsHeader title="Agents" sub="Loading…" />;
  }

  const activeRuns = workspaces.reduce((n, w) => n + (w.active_run_count || 0), 0);
  const selected = catalog.find((a) => a.key === selectedKey) || catalog[0];

  const toggle = async (agent, patch) => {
    try {
      await window.AtlasApi.fetch(`/api/agents/catalog/${agent.key}`, {
        method: 'PATCH', body: JSON.stringify(patch),
      });
      await reload();
      pushToast({ type: 'success', title: `Updated ${agent.label}` });
    } catch (e) {
      pushToast({ type: 'error', title: 'Update failed', body: e.message });
    }
  };

  return (
    <>
      <SettingsHeader
        title="Agents"
        sub={`${catalog.length} managed agents · ${workspaces.length} workspaces · ${activeRuns} active runs.`}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', minHeight: 'calc(100% - 92px)' }}>
        <div style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', overflow: 'auto' }}>
          {catalog.map((ag, i) => {
            const tone = HUE[toneFor(ag.key, i)];
            const sel = ag.key === selected?.key;
            const wsCount = wsByAgent[ag.key] || 0;
            return (
              <button key={ag.key} onClick={() => setSelectedKey(ag.key)} style={{
                display: 'flex', gap: 10, width: '100%', padding: '12px 16px',
                border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left',
                background: sel ? 'var(--surface-hover)' : 'transparent',
                borderLeft: sel ? '2px solid var(--accent)' : '2px solid transparent',
                opacity: ag.hidden ? 0.55 : 1,
              }}>
                <span style={{ width: 28, height: 28, borderRadius: 7, background: tone.bg, color: tone.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon.Bot size={14} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{ag.label}</span>
                    {!ag.enabled && <Pill hue="slate" sz="sm">paused</Pill>}
                    {ag.hidden && <Pill hue="rose" sz="sm">hidden</Pill>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <Pill hue={toneFor(ag.key, i)} sz="sm">{ag.category}</Pill>
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 4, display: 'flex', gap: 10 }} className="num">
                    {wsCount > 0
                      ? <span>{wsCount} workspace{wsCount === 1 ? '' : 's'}</span>
                      : <span style={{ color: 'var(--text-4)' }}>no workspaces yet</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ padding: '20px 28px', overflow: 'auto' }}>
          {selected && (
            <AgentConfigDetail
              agent={selected}
              wsCount={wsByAgent[selected.key] || 0}
              workspaces={workspaces.filter((w) => w.lead_agent_key === selected.key)}
              onToggle={toggle}
            />
          )}
        </div>
      </div>
    </>
  );
};

const AgentConfigDetail = ({ agent, wsCount, workspaces, onToggle }) => {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)' }}>{agent.label}</h2>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2, maxWidth: 600 }}>
            <code className="mono" style={{ padding: '0 4px', background: 'var(--surface-2)', borderRadius: 3 }}>{agent.key}</code>
            {' · '}{agent.category}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Toggle
            label={agent.enabled ? 'Enabled' : 'Paused'}
            on={agent.enabled}
            onChange={(v) => onToggle(agent, { enabled: v })}
          />
          <Toggle
            label={agent.hidden ? 'Hidden' : 'Visible'}
            on={!agent.hidden}
            tint={agent.hidden ? 'rose' : 'green'}
            onChange={(v) => onToggle(agent, { hidden: !v })}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ConfigField label="Description">
            <div style={{ padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
              {agent.description || <em style={{ color: 'var(--text-4)' }}>No description.</em>}
            </div>
          </ConfigField>

          <ConfigField label="Default goal">
            <div style={{ padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, fontSize: 12.5, color: 'var(--text-2)' }}>
              {agent.default_goal || <em style={{ color: 'var(--text-4)' }}>—</em>}
            </div>
          </ConfigField>

          {agent.best_for && agent.best_for.length > 0 && (
            <ConfigField label="Best for">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {agent.best_for.map((b) => <Pill key={b} hue="teal" sz="sm">{b}</Pill>)}
              </div>
            </ConfigField>
          )}

          <ConfigField label="Active workspaces">
            {workspaces.length === 0 && (
              <div style={{ padding: '10px 12px', background: 'var(--surface-2)', border: '1px dashed var(--border-strong)', borderRadius: 7, fontSize: 12, color: 'var(--text-3)' }}>
                No workspaces. Create one from a deal record to put this agent to work.
              </div>
            )}
            {workspaces.length > 0 && (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
                {workspaces.slice(0, 10).map((w, i) => (
                  <div key={w.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', borderTop: i === 0 ? 'none' : '1px solid var(--border)', fontSize: 12,
                  }}>
                    <span style={{ flex: 1, color: 'var(--text)', fontWeight: 500 }}>{w.name}</span>
                    <Pill hue={w.status === 'active' ? 'green' : 'slate'} sz="sm">{w.status}</Pill>
                    <span className="num" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                      {w.run_count} runs
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ConfigField>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, padding: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Live counts</div>
            <Limit label="Workspaces" value={String(wsCount)} />
            <Limit label="Total runs"  value={String(workspaces.reduce((n, w) => n + w.run_count, 0))} />
            <Limit label="Active runs" value={String(workspaces.reduce((n, w) => n + (w.active_run_count || 0), 0))} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Toggle = ({ label, on, onChange, tint }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
    <span style={{ fontSize: 12, color: on ? (tint === 'rose' ? 'var(--rose-fg)' : 'oklch(0.45 0.10 155)') : 'var(--text-3)' }}>{label}</span>
    <button onClick={() => onChange(!on)} style={{
      width: 32, height: 18, borderRadius: 999, padding: 1.5,
      background: on
        ? (tint === 'rose' ? 'oklch(0.55 0.14 25)' : 'oklch(0.55 0.12 155)')
        : 'oklch(0.85 0.005 270)',
      border: 'none', cursor: 'pointer', position: 'relative', transition: 'background .15s',
    }}>
      <span style={{
        display: 'block', width: 15, height: 15, borderRadius: 999, background: 'var(--surface)',
        transform: on ? 'translateX(14px)' : 'translateX(0)', transition: 'transform .15s', boxShadow: '0 1px 2px rgba(0,0,0,.15)',
      }} />
    </button>
  </div>
);

const ConfigField = ({ label, detail, children }) => (
  <div>
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
      {detail && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 1, maxWidth: 540 }}>{detail}</div>}
    </div>
    {children}
  </div>
);

const Limit = ({ label, value, warn }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', fontSize: 12 }}>
    <span style={{ color: 'var(--text-2)' }}>{label}</span>
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {warn && <span style={{ fontSize: 10.5, color: 'oklch(0.55 0.13 70)' }}>{warn}</span>}
      <span className="num" style={{ fontWeight: 500, color: 'var(--text)' }}>{value}</span>
    </span>
  </div>
);

// ============ MEMBERS ============
const MEMBERS = [
  { name: 'Andres Mehta',     email: 'andres@iprocapital.com',  role: 'Admin',          team: 'Investments', last: '2m ago',  avatar: 'oklch(0.50 0.14 30)' },
  { name: 'Jules Park',       email: 'jules@iprocapital.com',   role: 'Member',         team: 'Investments', last: '14m ago', avatar: 'oklch(0.50 0.13 200)' },
  { name: 'Lin Chen',         email: 'lin@iprocapital.com',     role: 'Member',         team: 'Investments', last: '1h ago',  avatar: 'oklch(0.50 0.13 295)' },
  { name: 'Marc Halloway',    email: 'marc@iprocapital.com',    role: 'Member',         team: 'Operations',  last: '3h ago',  avatar: 'oklch(0.50 0.13 145)' },
  { name: 'Priya Nair',       email: 'priya@iprocapital.com',   role: 'Admin',          team: 'IC',          last: 'yesterday', avatar: 'oklch(0.50 0.14 355)' },
  { name: 'IPro IC (group)',  email: 'ic@iprocapital.com',      role: 'Read-only',      team: 'IC',          last: '—',       avatar: 'oklch(0.55 0.005 270)', group: true },
];

// Pill colors for the Phase 3 role taxonomy.
const ROLE_HUE = {
  admin:            'amber',
  dealmaker:        'blue',
  senior_associate: 'violet',
  associate:        'teal',
  marketing:        'green',
  caller:           'rose',
  guest:            'slate',
};
const formatRole = (r) => (r === 'guest' ? 'deal guest' : (r || 'member').replace(/_/g, ' '));

// Stable hash → avatar color so each user gets a consistent dot.
const AVATAR_HUES = [
  'oklch(0.50 0.14 30)','oklch(0.50 0.13 200)','oklch(0.50 0.13 295)',
  'oklch(0.50 0.13 145)','oklch(0.50 0.14 355)','oklch(0.50 0.13 90)',
  'oklch(0.50 0.13 250)','oklch(0.50 0.14 60)',
];
const avatarColor = (key) => {
  let h = 0;
  for (let i = 0; i < (key || '').length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return AVATAR_HUES[Math.abs(h) % AVATAR_HUES.length];
};

const ROLE_OPTIONS = [
  { value: 'admin',            label: 'Admin' },
  { value: 'dealmaker',        label: 'Dealmaker' },
  { value: 'senior_associate', label: 'Senior Associate' },
  { value: 'associate',        label: 'Associate' },
  { value: 'marketing',        label: 'Marketing' },
  { value: 'caller',           label: 'Caller' },
  // Sees only the deals whose Deal team holds them, read-only (backend
  // app/deal_guest.py) — add them on each deal's Deal team row.
  { value: 'guest',            label: 'Deal guest' },
];

const adaptMember = (u) => ({
  id: u.id,
  name: u.full_name,
  email: u.email,
  role: u.role,
  active: u.active,
  team: u.team_name || u.office || '—',
  jobTitle: u.job_title,
  phone: u.phone,
  calendly: u.calendly_link,
  slack: u.slack_user_id,
  slackNotifications: u.slack_notifications !== false,
  ownerEligible: u.owner_eligible === true,
  watchMailbox: u.watch_mailbox === true,
  dealcloudId: u.dealcloud_id,
  avatar: avatarColor(u.email || u.id),
  raw: u,
});

// Fields a non-admin may edit on their own row — must stay in sync with
// _SELF_EDITABLE_FIELDS in backend routes/team.py (the API rejects the rest).
const SELF_EDITABLE_FIELDS = ['job_title', 'phone', 'calendly_link'];

const MembersSettings = ({ isAdmin, selfId }) => {
  const [members, setMembers] = React.useState(null); // null = loading
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [permsFor, setPermsFor] = React.useState(null);
  const [savingId, setSavingId] = React.useState(null);
  const canEdit = (m) => isAdmin || (selfId && String(m.id) === String(selfId));

  const reload = React.useCallback(async () => {
    const data = await window.AtlasData.loadTeamMembers();
    if (data == null) {
      setMembers(MEMBERS.map((m) => ({
        id: m.email, name: m.name, email: m.email,
        role: m.role.toLowerCase(), team: m.team, jobTitle: null,
        avatar: m.avatar, active: true, raw: null,
      })));
    } else {
      setMembers(data.map(adaptMember));
    }
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  // People who signed in with Microsoft but aren't on the roster yet — the
  // same requests the approver gets as Slack cards (app/access_requests.py).
  const [requests, setRequests] = React.useState([]);
  const [deciding, setDeciding] = React.useState(null);
  const reloadRequests = React.useCallback(() => {
    if (!isAdmin) return;
    window.AtlasApi.listAccessRequests().then((rows) => setRequests(rows || [])).catch(() => setRequests([]));
  }, [isAdmin]);
  React.useEffect(() => { reloadRequests(); }, [reloadRequests]);
  const decide = async (req, verb) => {
    if (verb === 'deny') {
      const ok = await showConfirm({
        title: `Deny ${req.full_name || req.email}?`,
        body: "They won't get into Atlas. An admin can clear it later.",
        confirmLabel: 'Deny',
        danger: true,
      });
      if (!ok) return;
    }
    setDeciding(req.id);
    try {
      const res = verb === 'approve'
        ? await window.AtlasApi.approveAccessRequest(req.id)
        : await window.AtlasApi.denyAccessRequest(req.id);
      pushToast({ type: 'success', title: res.message });
      reloadRequests();
      if (verb === 'approve') reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not update the request', body: e.message });
    } finally {
      setDeciding(null);
    }
  };

  const onSave = async (id, patch) => {
    if (!isAdmin) {
      // The backend 403s anything beyond the self-editable set; trim the
      // payload so an untouched Role/Email field can't fail the whole save.
      patch = Object.fromEntries(
        Object.entries(patch).filter(([k]) => SELF_EDITABLE_FIELDS.includes(k))
      );
    }
    setSavingId(id);
    try {
      const saved = await window.AtlasApi.updateTeamMember(id, patch);
      await reload();
      setEditing(null);
      pushToast({ type: 'success', title: 'Saved' });
      // Only set when the email changed: the API tried to create the matching
      // Supabase auth user (login needs it) and could not.
      if (saved && saved.auth_hint) {
        pushToast({ type: 'warning', title: 'Login not enabled yet', body: saved.auth_hint, duration: 12000 });
      }
      if (saved && saved.slack_hint) {
        pushToast({ type: 'warning', title: 'Slack ID not found', body: saved.slack_hint, duration: 12000 });
      }
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally {
      setSavingId(null);
    }
  };

  // "Invite member" — POST /team/members. The API also provisions the
  // Supabase Auth user (Microsoft sign-in) and, when no Slack ID was typed,
  // finds it from the email; each says so in a *_hint when it couldn't.
  const onCreate = async (form) => {
    const full_name = (form.full_name || '').trim();
    const email = (form.email || '').trim();
    if (!full_name) { pushToast({ type: 'error', title: 'Name is required' }); return; }
    if (!email) { pushToast({ type: 'error', title: 'Email is required', body: 'Use their @industrypro.com address — it has to match their Microsoft login.' }); return; }
    setSavingId('new');
    try {
      const created = await window.AtlasApi.createTeamMember({
        full_name,
        email,
        role: form.role || 'associate',
        job_title: form.job_title || null,
        phone: form.phone || null,
        team_name: form.team_name || null,
        calendly_link: form.calendly_link || null,
        slack_user_id: form.slack_user_id || null,
      });
      await reload();
      setCreating(false);
      pushToast({ type: 'success', title: `Added ${full_name}` });
      if (created && created.auth_hint) {
        pushToast({ type: 'warning', title: 'Login not enabled yet', body: created.auth_hint, duration: 12000 });
      }
      if (created && created.slack_hint) {
        pushToast({ type: 'warning', title: 'Slack ID not found', body: created.slack_hint, duration: 12000 });
      }
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not add member', body: e.message });
    } finally {
      setSavingId(null);
    }
  };

  const onDeactivate = async (id) => {
    const ok = await showConfirm({
      title: 'Deactivate this member?',
      body: 'They can be restored later from this same view.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!ok) return;
    setSavingId(id);
    try {
      await window.AtlasApi.deactivateTeamMember(id);
      await reload();
      setEditing(null);
    } finally { setSavingId(null); }
  };

  const onRestore = async (id) => {
    setSavingId(id);
    try {
      await window.AtlasApi.restoreTeamMember(id);
      await reload();
    } finally { setSavingId(null); }
  };

  const isLoading = members === null;
  const list = members || [];
  // Active and inactive members live on separate tabs; deactivated people
  // were drowning the current roster (55 rows, roughly half gone).
  const [tab, setTab] = React.useState('active');
  const activeList = list.filter((m) => m.active !== false);
  const inactiveList = list.filter((m) => m.active === false);
  const shown = tab === 'inactive' ? inactiveList : activeList;
  const tabBtn = (key, label, count) => (
    <button key={key} onClick={() => setTab(key)} style={{
      fontSize: 12.5, fontWeight: tab === key ? 600 : 400, padding: '6px 12px', cursor: 'pointer',
      border: 'none', borderBottom: '2px solid ' + (tab === key ? 'var(--accent-text)' : 'transparent'),
      background: 'none', color: tab === key ? 'var(--text)' : 'var(--text-3)' }}>
      {label} <span style={{ color: 'var(--text-4)', fontWeight: 400 }}>{count}</span>
    </button>
  );

  return (
    <>
      <SettingsHeader
        title="Members & teams"
        sub={`${activeList.length} active users`}
        action={isAdmin ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />} onClick={() => setCreating(true)}>Invite member</Btn>
          </div>
        ) : null}
      />
      <div style={{ padding: '20px 28px' }}>
        {isAdmin && requests.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ padding: '8px 14px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
              Access requests · {requests.length}
            </div>
            {requests.map((r) => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid var(--border)' }}>
                <Avatar name={r.full_name || r.email} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>
                    {r.full_name || r.email}
                    <span style={{ fontWeight: 400, color: 'var(--text-3)' }}> · {r.email}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }} title={r.note || ''}>
                    Asked: {[r.position, r.team, r.reports_to && `reports to ${r.reports_to}`].filter(Boolean).join(' · ')}
                    {' — '}Approve sets up: {r.plan}
                  </div>
                </div>
                <Btn size="sm" variant="ghost" onClick={() => decide(r, 'deny')} disabled={deciding === r.id}>Deny</Btn>
                <Btn size="sm" variant="accent" onClick={() => decide(r, 'approve')} disabled={deciding === r.id}>
                  {deciding === r.id ? 'Working…' : 'Approve'}
                </Btn>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 12 }}>
          {tabBtn('active', 'Active', activeList.length)}
          {tabBtn('inactive', 'Inactive', inactiveList.length)}
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
          <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 2, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Member</div>
            <div style={{ width: 140, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Role</div>
            <div style={{ width: 170, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Team / office</div>
            <div style={{ width: 200, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Job title</div>
            <div style={{ width: 36 }} />
          </div>
          {isLoading && (
            <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)' }}>Loading members…</div>
          )}
          {!isLoading && shown.length === 0 && (
            <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)' }}>
              {tab === 'inactive' ? 'No inactive members.' : 'No active members.'}
            </div>
          )}
          {shown.map((m) => (
            <div key={m.id}
              onClick={() => m.raw && canEdit(m) && setEditing(m)}
              style={{
                display: 'flex', minHeight: 44, alignItems: 'center',
                borderBottom: '1px solid var(--border)',
                cursor: m.raw && canEdit(m) ? 'pointer' : 'default',
              }}
              onMouseEnter={(e) => { if (m.raw && canEdit(m)) e.currentTarget.style.background = 'var(--surface-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ flex: 2, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar name={m.name} size={26} color={m.avatar} />
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {m.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{m.email}</div>
                </div>
              </div>
              <div style={{ width: 140, padding: '0 10px' }}>
                <Pill hue={ROLE_HUE[m.role] || 'slate'} sz="sm">{formatRole(m.role)}</Pill>
              </div>
              <div style={{ width: 170, padding: '0 10px', fontSize: 12, color: 'var(--text-2)' }}>{m.team}</div>
              <div style={{ width: 200, padding: '0 10px', fontSize: 12, color: 'var(--text-2)' }}>{m.jobTitle || '—'}</div>
              <div style={{ width: 36, display: 'flex', justifyContent: 'center' }}>
                {m.active === false
                  ? (isAdmin && <button onClick={(e) => { e.stopPropagation(); onRestore(m.id); }} title="Restore" style={{ width: 22, height: 22, borderRadius: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 12 }}>↺</button>)
                  : (canEdit(m) && <Icon.ChevronR size={12} color="var(--text-4)" />)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <MemberDrawer
          member={editing}
          limited={!isAdmin}
          saving={savingId === editing.id}
          onClose={() => setEditing(null)}
          onSave={(patch) => onSave(editing.id, patch)}
          onDeactivate={() => onDeactivate(editing.id)}
          onPermissions={isAdmin ? () => { setPermsFor(editing); setEditing(null); } : null}
          onSlackFound={reload}
        />
      )}

      {creating && isAdmin && (
        <MemberDrawer
          member={{}}
          isNew
          saving={savingId === 'new'}
          onClose={() => setCreating(false)}
          onSave={onCreate}
        />
      )}

      {permsFor && (
        <MemberPermissionsDrawer
          member={permsFor}
          isSelf={!!selfId && String(permsFor.id) === String(selfId)}
          onClose={() => setPermsFor(null)}
        />
      )}
    </>
  );
};

const MemberDrawer = ({ member, limited, isNew = false, saving, onClose, onSave, onDeactivate, onPermissions, onSlackFound }) => {
  const [form, setForm] = React.useState({
    full_name:     member.raw?.full_name     || member.name || '',
    email:         member.raw?.email         || member.email || '',
    role:          member.raw?.role          || member.role || 'associate',
    job_title:     member.raw?.job_title     || '',
    phone:         member.raw?.phone         || '',
    team_name:     member.raw?.team_name     || '',
    calendly_link: member.raw?.calendly_link || '',
    slack_user_id: member.raw?.slack_user_id || '',
    slack_notifications: member.raw?.slack_notifications !== false,
    owner_eligible: member.raw?.owner_eligible === true,
    watch_mailbox: member.raw?.watch_mailbox === true,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // "Find in Slack" — the API looks their email up in the workspace and
  // saves the id straight away (no Save needed).
  const [findingSlack, setFindingSlack] = React.useState(false);
  const findSlack = async () => {
    setFindingSlack(true);
    try {
      const found = await window.AtlasApi.lookupTeamMemberSlack(member.id);
      setForm((f) => ({ ...f, slack_user_id: found.slack_user_id || '' }));
      pushToast({ type: 'success', title: 'Slack ID found', body: found.slack_user_id });
      if (onSlackFound) onSlackFound();
    } catch (e) {
      pushToast({ type: 'warning', title: 'Slack ID not found', body: e.message, duration: 12000 });
    } finally {
      setFindingSlack(false);
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={isNew ? 'Invite member' : (member.name || 'Edit member')}
      subtitle={isNew ? undefined : member.email}
      footer={(
        <>
          {!limited && !isNew && (
            <Btn size="sm" variant="ghost" onClick={onDeactivate} disabled={saving}>
              {member.active === false ? 'Restore' : 'Deactivate'}
            </Btn>
          )}
          {onPermissions && (
            <Btn size="sm" variant="ghost" onClick={onPermissions} disabled={saving}>Permissions</Btn>
          )}
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={() => onSave(form)} disabled={saving}>
            {saving ? 'Saving…' : (isNew ? 'Add member' : 'Save')}
          </Btn>
        </>
      )}
    >
      {limited && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 10 }}>
          Editing your own profile. Name, email, role, and team are managed by an admin.
        </div>
      )}
      {!limited && (
        <>
          <Field label="Full name"><TextInput value={form.full_name} onChange={set('full_name')} /></Field>
          <Field label="Email"><TextInput value={form.email} onChange={set('email')} /></Field>
          <Field label="Role">
            {/* A deal guest can't see what an owner is handed — switching to it
                turns owner routing off (the API does the same). */}
            <Select value={form.role} options={ROLE_OPTIONS}
              onChange={(e) => { const role = e.target.value; setForm((f) => ({ ...f, role, ...(role === 'guest' ? { owner_eligible: false } : null) })); }} />
          </Field>
          <Field label="Team (display label)" hint="Shown in lists. Membership is managed in the Teams pane.">
            <TextInput value={form.team_name} onChange={set('team_name')} />
          </Field>
        </>
      )}
      <Field label="Job title"><TextInput value={form.job_title} onChange={set('job_title')} /></Field>
      <Field label="Phone"><TextInput value={form.phone} onChange={set('phone')} /></Field>
      <Field label="Calendly link"><TextInput value={form.calendly_link} onChange={set('calendly_link')} /></Field>
      {!limited && (
        <>
          <Field
            label="Slack user ID"
            hint={isNew
              ? 'Leave blank to have Atlas find it from their email. Used for Ask Atlas in Slack, inbox receipts, the daily brief.'
              : 'e.g. U0123ABC — Ask Atlas in Slack, inbox receipts, the daily brief.'}
          >
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <TextInput value={form.slack_user_id} onChange={set('slack_user_id')} />
              </div>
              {!isNew && member.id && (
                <Btn size="sm" variant="outline" onClick={findSlack} disabled={saving || findingSlack || !form.email}>
                  {findingSlack ? 'Finding…' : 'Find in Slack'}
                </Btn>
              )}
            </div>
          </Field>
          <Field
            label="Slack notifications"
            hint="Off = Atlas never DMs this account unprompted (daily brief, release notes). Replies to their own messages still arrive. Keep off for shared or service accounts."
          >
            <Toggle
              label={form.slack_notifications ? 'On' : 'Off'}
              on={form.slack_notifications}
              onChange={(v) => setForm((f) => ({ ...f, slack_notifications: v }))}
            />
          </Field>
          <Field
            label="Can own records"
            hint="Appears in the owner pickers for companies, contacts and deals (and in bulk reassign). Dealmakers and the admins who run a book; not associates, marketing or service accounts."
          >
            <Toggle
              label={form.owner_eligible ? 'Yes' : 'No'}
              on={form.owner_eligible}
              onChange={(v) => setForm((f) => ({ ...f, owner_eligible: v }))}
            />
          </Field>
          <Field
            label="Keep watching mailbox after departure"
            hint="When this person is deactivated, their Outlook/Fireflies sync stays on (moved to the organization grant) so coverage watch still sees replies and calendar invites. For departed dealmakers whose inbox still matters."
          >
            <Toggle
              label={form.watch_mailbox ? 'On' : 'Off'}
              on={form.watch_mailbox}
              onChange={(v) => setForm((f) => ({ ...f, watch_mailbox: v }))}
            />
          </Field>
        </>
      )}
      {member.dealcloudId && (
        <div style={{ fontSize: 10.5, color: 'var(--text-4)', fontFamily: 'var(--font-mono)', marginTop: 8 }}>
          dealcloud_id: {member.dealcloudId}
        </div>
      )}
    </Drawer>
  );
};


// ============ PER-MEMBER PERMISSIONS ============
// Capabilities normally follow the roster role (every provisioned member is at
// least a Member/editor). This drawer writes explicit exceptions to
// team_member_permissions — the "keeps working, can't delete" case for someone
// on their way out, or handing one admin capability to one associate.

// null = inherit the role default, true = allow, false = deny.
const PERM_CHOICES = [
  { value: null,  label: 'Inherit' },
  { value: true,  label: 'Allow'   },
  { value: false, label: 'Deny'    },
];

const PermChoice = ({ value, roleDefault, onChange, disabled }) => (
  <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
    {PERM_CHOICES.map((c) => {
      const on = c.value === value;
      // "Inherit" spells out what it resolves to so the admin doesn't have to
      // hold the role lattice in their head.
      const label = c.value === null && !on
        ? 'Inherit'
        : (c.value === null ? `Inherit · ${roleDefault ? 'allowed' : 'denied'}` : c.label);
      const hue = c.value === false ? 'var(--rose-fg)' : 'var(--text)';
      return (
        <button
          key={String(c.value)}
          onClick={() => !disabled && onChange(c.value)}
          disabled={disabled}
          style={{
            padding: '3px 9px', fontSize: 11, lineHeight: 1.5, whiteSpace: 'nowrap',
            border: 'none', borderLeft: c.value === null ? 'none' : '1px solid var(--border)',
            background: on ? 'var(--surface-2)' : 'transparent',
            color: on ? hue : 'var(--text-4)',
            fontWeight: on ? 600 : 400,
            cursor: disabled ? 'default' : 'pointer',
          }}
        >{label}</button>
      );
    })}
  </div>
);

const MemberPermissionsDrawer = ({ member, isSelf, onClose }) => {
  const [data, setData]     = React.useState(null);
  const [draft, setDraft]   = React.useState({});   // key -> null|true|false
  const [reason, setReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr]       = React.useState(null);

  React.useEffect(() => {
    let live = true;
    window.AtlasApi.memberPermissions(member.id)
      .then((d) => { if (live) { setData(d); setDraft({}); } })
      .catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [member.id]);

  const valueFor = (p) => (p.key in draft ? draft[p.key] : (p.override ?? null));
  const dirty = Object.keys(draft).length > 0;

  const groups = React.useMemo(() => {
    const out = new Map();
    for (const p of data?.permissions || []) {
      if (!out.has(p.group)) out.set(p.group, []);
      out.get(p.group).push(p);
    }
    return [...out.entries()];
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await window.AtlasApi.setMemberPermissions(member.id, draft, reason);
      setData({ ...data, permissions: res.permissions });
      setDraft({});
      setReason('');
      pushToast({
        type: 'success',
        title: res.changed ? `${res.changed} permission${res.changed === 1 ? '' : 's'} updated` : 'No changes',
        body: res.changed ? 'Takes effect within a minute.' : undefined,
      });
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save', body: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={`Permissions · ${member.name}`}
      subtitle={data ? `Role: ${formatRole(member.role)}` : member.email}
      footer={(
        <>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Close</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save changes'}
          </Btn>
        </>
      )}
    >
      {err && <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>Couldn't load permissions: {err}</div>}
      {!data && !err && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {data && (
        <>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.55, marginBottom: 12 }}>
            Leave everything on <b>Inherit</b> for a normal member. <b>Deny</b> takes a capability
            away that their role would otherwise grant; <b>Allow</b> hands them one above their
            role. Denying <b>View CRM data</b> suspends the account entirely — every request is
            refused until it's restored.
          </div>
          {isSelf && (
            <div style={{ fontSize: 11.5, color: 'var(--amber-fg)', marginBottom: 12 }}>
              This is your own account. You can grant yourself capabilities but not revoke
              them — ask another admin.
            </div>
          )}
          {groups.map(([group, perms]) => (
            <div key={group} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-4)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>{group}</div>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
                {perms.map((p, i) => {
                  const v = valueFor(p);
                  const changed = p.key in draft && draft[p.key] !== (p.override ?? null);
                  return (
                    <div key={p.key} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px',
                      borderTop: i ? '1px solid var(--border)' : 'none',
                      background: changed ? 'var(--surface-2)' : 'transparent',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{p.label}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 1 }}>{p.description}</div>
                      </div>
                      <PermChoice
                        value={v}
                        roleDefault={p.role_default}
                        disabled={isSelf}
                        onChange={(next) => setDraft((d) => ({ ...d, [p.key]: next }))}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <Field label="Reason (optional)" hint="Recorded on the audit trail next to each change.">
            <TextInput value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. offboarding — read/edit only until last day" />
          </Field>
        </>
      )}
    </Drawer>
  );
};


// ============ TEAMS ============

const TEAM_HUE = ['blue', 'teal', 'violet', 'amber'];

const TeamsSettings = () => {
  const [teams, setTeams]     = React.useState(null);
  const [members, setMembers] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const reload = React.useCallback(async () => {
    const [t, m] = await Promise.all([
      window.AtlasApi.listTeams().catch(() => null),
      window.AtlasData.loadTeamMembers(),
    ]);
    setTeams(t);
    setMembers(Array.isArray(m) ? m : []);
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  if (teams === null) {
    return (
      <>
        <SettingsHeader title="Teams" sub="Loading…" />
      </>
    );
  }
  if (!teams) {
    return (
      <>
        <SettingsHeader title="Teams" sub="Couldn't reach API. Teams require live backend." />
      </>
    );
  }

  return (
    <>
      <SettingsHeader
        title="Teams"
        sub={`${teams.length} teams · ${teams.reduce((n, t) => n + t.members.length, 0)} memberships`}
        action={(
          <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />} onClick={() => setCreating(true)}>New team</Btn>
        )}
      />
      <div style={{ padding: '20px 28px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 14 }}>
        {teams.map((t, i) => (
          <button key={t.id}
            onClick={() => setEditing(t)}
            style={{
              textAlign: 'left', cursor: 'pointer',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
              padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
              transition: 'border-color .12s, box-shadow .12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar name={t.director_name || t.name} size={32} color={HUE[TEAM_HUE[i % TEAM_HUE.length]].dot} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{t.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                  Director · <UserLink id={t.director_id} name={t.director_name} style={{ color: 'inherit' }} />
                </div>
              </div>
              {!t.active && <Pill hue="slate" sz="sm">inactive</Pill>}
            </div>

            <div>
              <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
                Members ({t.members.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {t.members.map((m) => (
                  <span key={m.id} title={`${m.user_name} · ${formatRole(m.role_in_team)}${m.supports_user_name ? ' (supports ' + m.supports_user_name + ')' : ''}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: 'var(--surface-2)', border: '1px solid var(--border)',
                      borderRadius: 999, padding: '2px 8px 2px 3px',
                      fontSize: 11, color: 'var(--text-2)',
                    }}
                  >
                    <Avatar name={m.user_name} size={16} color={avatarColor(m.user_email || m.user_id)} />
                    <UserLink id={m.user_id} name={m.user_name} style={{ color: 'inherit' }} />
                    <Pill hue={ROLE_HUE[m.role_in_team === 'director' ? 'dealmaker' : m.role_in_team] || 'slate'} sz="sm">
                      {m.role_in_team === 'director' ? 'dir' : m.role_in_team === 'senior_associate' ? 'sr' : 'as'}
                    </Pill>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
                Industries ({t.industries.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {t.industries.map((name) => (
                  <Pill key={name} hue="teal" sz="sm">{name}</Pill>
                ))}
                {t.industries.length === 0 && (
                  <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>No industries assigned.</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {editing && (
        <TeamDrawer
          team={teams.find((t) => t.id === editing.id) || editing}
          allMembers={members}
          onClose={() => setEditing(null)}
          onChange={async () => { await reload(); }}
          onDeleted={async () => { setEditing(null); await reload(); }}
        />
      )}
      {creating && (
        <NewTeamDrawer
          allMembers={members}
          onClose={() => setCreating(false)}
          onCreated={async () => { setCreating(false); await reload(); }}
        />
      )}
    </>
  );
};

// "New team" — POST /teams. Slug is derived server-side from the name.
const NewTeamDrawer = ({ allMembers, onClose, onCreated }) => {
  const [name, setName]         = React.useState('');
  const [director, setDirector] = React.useState('');
  const [desc, setDesc]         = React.useState('');
  const [saving, setSaving]     = React.useState(false);

  const directorOptions = [
    { value: '', label: '— none yet —' },
    ...allMembers
      .filter((u) => u.active !== false)
      .map((u) => ({ value: u.id, label: u.full_name })),
  ];

  const onSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { pushToast({ type: 'error', title: 'Team name is required' }); return; }
    setSaving(true);
    try {
      await window.AtlasApi.createTeam({
        name: trimmed,
        director_id: director || null,
        description: desc.trim() || null,
      });
      pushToast({ type: 'success', title: `Created ${trimmed}`, body: 'Add members from the team card.' });
      onCreated();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not create team', body: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title="New team"
      width={460}
      footer={(
        <>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={onSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create team'}
          </Btn>
        </>
      )}
    >
      <Field label="Team name"><TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sebastian team" /></Field>
      <Field label="Director" hint="Owns the team's records; can be set later.">
        <Select value={director} onChange={(e) => setDirector(e.target.value)} options={directorOptions} />
      </Field>
      <Field label="Description"><TextInput value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" /></Field>
    </Drawer>
  );
};

const TeamDrawer = ({ team, allMembers, onClose, onChange, onDeleted }) => {
  const [adding, setAdding] = React.useState(false);
  const [name, setName]         = React.useState(team.name);
  const [director, setDirector] = React.useState(team.director_id || '');
  const [desc, setDesc]         = React.useState(team.description || '');
  const [saving, setSaving]     = React.useState(false);
  // The parent re-fetches after every membership change and hands us the
  // fresh team; resync the form so a saved rename doesn't snap back.
  React.useEffect(() => {
    setName(team.name);
    setDirector(team.director_id || '');
    setDesc(team.description || '');
  }, [team.id, team.name, team.director_id, team.description]);
  const dirty = name.trim() !== team.name
    || (director || '') !== (team.director_id || '')
    || desc.trim() !== (team.description || '');
  // Active members, plus the current director even if they've since left
  // (the picker must be able to show who it is before you change it).
  const directorOptions = [
    { value: '', label: '— none —' },
    ...allMembers
      .filter((u) => u.active !== false || u.id === team.director_id)
      .map((u) => ({ value: u.id, label: u.full_name + (u.active === false ? ' (inactive)' : '') })),
  ];

  const onSaveTeam = async () => {
    const trimmed = name.trim();
    if (!trimmed) { pushToast({ type: 'error', title: 'Team name is required' }); return; }
    setSaving(true);
    try {
      await window.AtlasApi.updateTeam(team.id, {
        name: trimmed,
        director_id: director || null,
        description: desc.trim() || null,
      });
      pushToast({ type: 'success', title: 'Team updated' });
      onChange();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save team', body: e.message });
    } finally {
      setSaving(false);
    }
  };

  const onDeleteTeam = async () => {
    const ok = await showConfirm({
      title: `Delete ${team.name}?`,
      body: `Removes the team and its ${team.members.length} membership${team.members.length === 1 ? '' : 's'}. Members keep their accounts and industries; only the grouping goes away.`,
      confirmLabel: 'Delete team',
      danger: true,
    });
    if (!ok) return;
    try {
      await window.AtlasApi.deleteTeam(team.id, { hard: true });
      pushToast({ type: 'success', title: `Deleted ${team.name}` });
      onDeleted();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not delete team', body: e.message });
    }
  };
  const [pickUser, setPickUser] = React.useState('');
  const [pickRole, setPickRole] = React.useState('associate');
  const [pickSupports, setPickSupports] = React.useState('');

  const memberIds = new Set(team.members.map((m) => m.user_id));
  const candidates = allMembers.filter((u) => !memberIds.has(u.id) && u.active !== false);
  const supportsCandidates = team.members.filter((m) => m.role_in_team !== 'associate');

  const onRemove = async (membershipId) => {
    const ok = await showConfirm({
      title: 'Remove from team?',
      body: 'They lose visibility into this team\'s records but their account stays active.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    await window.AtlasApi.removeTeamMembership(team.id, membershipId);
    pushToast({ type: 'success', title: 'Removed from team' });
    onChange();
  };

  const onChangeRole = async (membershipId, newRole) => {
    await window.AtlasApi.updateTeamMembership(team.id, membershipId, { role_in_team: newRole });
    onChange();
  };

  const onAdd = async () => {
    if (!pickUser) return;
    try {
      await window.AtlasApi.addTeamMembership(team.id, {
        user_id: pickUser,
        role_in_team: pickRole,
        supports_user_id: pickSupports || null,
      });
      setAdding(false);
      setPickUser(''); setPickSupports('');
      pushToast({ type: 'success', title: 'Added to team' });
      onChange();
    } catch (e) {
      pushToast({ type: 'error', title: 'Add failed', body: e.message });
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={team.name}
      subtitle={team.description || '—'}
      width={520}
      footer={(
        <>
          <Btn size="sm" variant="ghost" onClick={onDeleteTeam} disabled={saving}
            style={{ color: 'var(--danger, #c0392b)' }}>Delete team</Btn>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Done</Btn>
          {dirty && (
            <Btn size="sm" variant="accent" onClick={onSaveTeam} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Btn>
          )}
        </>
      )}
    >
      <Field label="Team name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Director" hint="Owns the team's records. Change it here when a team changes hands.">
        <Select value={director} onChange={(e) => setDirector(e.target.value)} options={directorOptions} />
      </Field>
      <Field label="Description">
        <TextInput value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" />
      </Field>

      <div style={{ marginTop: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>Members</div>
        <div style={{ flex: 1 }} />
        {!adding && <Btn size="xs" variant="outline" icon={<Icon.Plus size={11} />} onClick={() => setAdding(true)}>Add member</Btn>}
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, marginBottom: 12 }}>
        {team.members.map((m, i) => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
            <Avatar name={m.user_name} size={22} color={avatarColor(m.user_email || m.user_id)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{m.user_name}</div>
              {m.supports_user_name && (
                <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>supports {m.supports_user_name}</div>
              )}
            </div>
            <div style={{ width: 130 }}>
              <Combobox
                value={m.role_in_team}
                onChange={(e) => onChangeRole(m.id, e.target.value)}
                options={[
                  { value: 'director',         label: 'Director' },
                  { value: 'senior_associate', label: 'Sr Associate' },
                  { value: 'associate',        label: 'Associate' },
                ]}
              />
            </div>
            <button onClick={() => onRemove(m.id)} title="Remove" style={{
              width: 22, height: 22, borderRadius: 4, background: 'transparent', border: 'none',
              cursor: 'pointer', color: 'var(--text-4)', fontSize: 14,
            }}>×</button>
          </div>
        ))}
      </div>

      {adding && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7, padding: 10 }}>
          <Field label="User">
            <Select
              value={pickUser}
              onChange={(e) => setPickUser(e.target.value)}
              options={[{ value: '', label: 'Pick a member…' }, ...candidates.map((u) => ({ value: u.id, label: u.full_name }))]}
            />
          </Field>
          <Field label="Role in team">
            <Select
              value={pickRole}
              onChange={(e) => setPickRole(e.target.value)}
              options={[
                { value: 'director',         label: 'Director' },
                { value: 'senior_associate', label: 'Senior Associate' },
                { value: 'associate',        label: 'Associate' },
              ]}
            />
          </Field>
          {pickRole === 'associate' && (
            <Field label="Supports (optional)" hint="Leave blank if the associate supports the whole team.">
              <Select
                value={pickSupports}
                onChange={(e) => setPickSupports(e.target.value)}
                options={[{ value: '', label: '(no specific senior)' }, ...supportsCandidates.map((m) => ({ value: m.user_id, label: m.user_name }))]}
              />
            </Field>
          )}
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <Btn size="xs" variant="ghost" onClick={() => setAdding(false)}>Cancel</Btn>
            <Btn size="xs" variant="accent" onClick={onAdd}>Add</Btn>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
          Industry coverage ({team.industries.length})
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {team.industries.map((n) => <Pill key={n} hue="teal" sz="sm">{n}</Pill>)}
          {team.industries.length === 0 && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>No industries assigned to any team member.</span>}
        </div>
      </div>
    </Drawer>
  );
};

// ============ INDUSTRIES ============

const IndustriesSettings = () => {
  const [industries, setIndustries] = React.useState(null);
  const [members,    setMembers]    = React.useState([]);
  const [editing,    setEditing]    = React.useState(null);
  const [creating,   setCreating]   = React.useState(false);

  const reload = React.useCallback(async () => {
    const [inds, mems] = await Promise.all([
      window.AtlasApi.listIndustries().catch(() => null),
      window.AtlasData.loadTeamMembers(),
    ]);
    setIndustries(inds);
    setMembers(Array.isArray(mems) ? mems : []);
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  if (industries === null) {
    return <SettingsHeader title="Industries" sub="Loading…" />;
  }

  return (
    <>
      <SettingsHeader
        title="Industries"
        action={(
          <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />} onClick={() => setCreating(true)}>
            New industry
          </Btn>
        )}
      />
      <div style={{ padding: '20px 28px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
          <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 2, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Industry</div>
            <div style={{ width: 260, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Aliases</div>
            <div style={{ width: 200, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Owner</div>
            <div style={{ width: 160, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Team</div>
            <div style={{ width: 70,  padding: '0 10px', display: 'flex', alignItems: 'center' }}>Status</div>
          </div>
          {industries.map((i) => (
            <div key={i.id}
              onClick={() => setEditing(i)}
              style={{
                display: 'flex', minHeight: 38, alignItems: 'center', cursor: 'pointer',
                borderBottom: '1px solid var(--border)',
                opacity: i.active ? 1 : 0.5,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ flex: 2, padding: '6px 14px', fontSize: 12.5, color: 'var(--text)', fontWeight: 500 }}>{i.name}</div>
              <div style={{ width: 260, padding: '6px 10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                {(i.aliases || []).length
                  ? (
                    <>
                      {i.aliases.slice(0, 3).map((a) => (
                        <Pill key={a.id} hue="slate" sz="sm" maxW={230} title={a.alias}>{a.alias}</Pill>
                      ))}
                      {i.aliases.length > 3 && (
                        <span style={{ fontSize: 11, color: 'var(--text-4)' }}>+{i.aliases.length - 3}</span>
                      )}
                    </>
                  )
                  : <span style={{ fontSize: 12, color: 'var(--text-4)' }}>—</span>}
              </div>
              <div style={{ width: 200, padding: '0 10px', fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                {i.assigned_user_name && <Avatar name={i.assigned_user_name} size={16} color={avatarColor(i.assigned_user_id)} />}
                {i.assigned_user_name
                  ? <UserLink id={i.assigned_user_id} name={i.assigned_user_name} style={{ color: 'inherit' }} />
                  : <span style={{ color: 'var(--text-4)' }}>unassigned</span>}
              </div>
              <div style={{ width: 160, padding: '0 10px', fontSize: 12, color: 'var(--text-2)' }}>
                {i.assigned_team_name || '—'}
              </div>
              <div style={{ width: 70, padding: '0 10px' }}>
                {i.active
                  ? <Pill hue="green" dot sz="sm">active</Pill>
                  : <Pill hue="slate" sz="sm">off</Pill>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {(editing || creating) && (
        <IndustryDrawer
          industry={editing}
          isNew={creating}
          allMembers={members}
          onClose={() => { setEditing(null); setCreating(false); }}
          onChange={async () => { await reload(); }}
        />
      )}
    </>
  );
};

// ── Industry rules (202610052100) ───────────────────────────────────────────
// Who is a target, who is not, the size criteria, the revenue floor and the
// valuation rubric — the associates' rulebook, imported from "Industry
// Targets.xlsx" and "Industry Overviews.pdf", editable here. The rubric is
// read-only: it comes from the deck and is re-read, not hand-edited.
// The industry page (views/industries.jsx) reuses it: `readOnly` for anyone
// without Manage industries, `hideRubric` because the page shows the deck
// itself under Valuation.
const RULE_GEO = [['US', 'United States'], ['CA', 'Canada'], ['GB', 'UK'], ['AU', 'Australia'], ['NZ', 'New Zealand']];
const RULE_TIERS = [['qualified', 'Qualified'], ['strong', 'Strong'], ['premium', 'Premium']];
const IndustryRulesBlock = ({ industry, readOnly = false, hideRubric = false }) => {
  const [p, setP] = React.useState(undefined);     // undefined loading · null none
  const [f, setF] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [showRubric, setShowRubric] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.getIndustryProfile(industry.id)
      .then((r) => { if (alive) { setP(r); setF(fromProfile(r)); } })
      .catch(() => { if (alive) { setP(null); setF(fromProfile(null)); } });
    return () => { alive = false; };
  }, [industry.id]);
  const fromProfile = (r) => ({
    target: (r && r.target) || '', exclusions: (r && r.exclusions) || '', details: (r && r.details) || '',
    criteria_text: (r && r.criteria_text) || '', revenue_min_usd: r && r.revenue_min_usd != null ? r.revenue_min_usd : null,
    geography_codes: (r && r.geography_codes) || [], geography_text: (r && r.geography_text) || '', prospect_type: (r && r.prospect_type) || '',
    ...Object.fromEntries(RULE_TIERS.map(([t]) => [`sig_${t}`, ((r && r.size_signals && r.size_signals[t]) || []).join('\n')])),
  });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e && e.target ? e.target.value : e }));
  const dirty = p !== undefined && f && JSON.stringify(f) !== JSON.stringify(fromProfile(p));
  const save = async () => {
    setBusy(true);
    try {
      const lines = (v) => v.split('\n').map((x) => x.trim()).filter(Boolean);
      const sig = Object.fromEntries(RULE_TIERS.map(([t]) => [t, lines(f[`sig_${t}`])]));
      const rest = Object.fromEntries(Object.entries(f).filter(([k]) => !k.startsWith('sig_')));
      const saved = await window.AtlasApi.saveIndustryProfile(industry.id, {
        ...rest, prospect_type: f.prospect_type || null, revenue_min_usd: f.revenue_min_usd === '' ? null : f.revenue_min_usd,
        geography_text: f.geography_text.trim() || null,
        size_signals: Object.values(sig).some((xs) => xs.length) ? sig : null,
      });
      setP(saved); setF(fromProfile(saved));
      pushToast({ type: 'success', title: 'Rules saved' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save rules', body: e.message });
    } finally { setBusy(false); }
  };
  if (p === undefined || !f) return <div style={{ fontSize: 12, color: 'var(--text-4)', marginBottom: 12 }}>Loading rules…</div>;
  const ta = (k, rows, ph) => (
    <textarea value={f[k]} onChange={set(k)} rows={rows} placeholder={readOnly ? '' : ph} readOnly={readOnly} style={{
      width: '100%', padding: '6px 8px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 5,
      fontFamily: 'inherit', resize: readOnly ? 'none' : 'vertical', background: readOnly ? 'var(--surface-2)' : 'var(--surface)', color: 'var(--text)',
    }} />
  );
  const rub = !hideRubric && p && p.rubric;
  const sources = (p && p.sources) || [];
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>Rules</span>
        {p && p.dealmaker_name && <Pill hue="slate" sz="sm">{p.dealmaker_name}{p.prospect_type ? ` · ${p.prospect_type.toLowerCase()}` : ''}</Pill>}
        {sources.length > 0 && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>from {sources.join(', ')}</span>}
        {!p && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{readOnly ? 'nothing recorded yet' : 'nothing recorded yet — write the rule here'}</span>}
        <span style={{ flex: 1 }} />
        {dirty && !readOnly && <Btn size="xs" variant="accent" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save rules'}</Btn>}
      </div>
      <Field label="Who is a target" hint="One sentence. The qualify step reads this, not the industry name.">
        {ta('target', 3, 'e.g. General and specialized auto repair shops: oil change, quick lube, tire, brakes…')}
      </Field>
      <Field label="Who is not">
        {ta('exclusions', 2, 'e.g. Auto detailing, car washes, dealerships, resale')}
      </Field>
      <Field label="Details" hint="The paragraph the associates wrote — what the business does and for whom.">
        {ta('details', 4, '')}
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
        <Field label="Revenue floor" hint="Dollars. Blank = no minimum.">
          <MoneyInput value={f.revenue_min_usd} onChange={set('revenue_min_usd')} placeholder={readOnly ? '' : 'e.g. 3M'} disabled={readOnly} />
        </Field>
        <Field label="Geography">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 4 }}>
            {RULE_GEO.map(([code, label]) => {
              const on = f.geography_codes.includes(code);
              return (
                <button key={code} type="button" disabled={readOnly} aria-pressed={on} onClick={() => set('geography_codes')(on ? f.geography_codes.filter((c) => c !== code) : [...f.geography_codes, code])}
                  style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 999, border: '1px solid', cursor: readOnly ? 'default' : 'pointer',
                           borderColor: on ? 'var(--text)' : 'var(--border)', background: on ? 'var(--text)' : 'transparent', color: on ? 'var(--surface)' : 'var(--text-3)' }}>
                  {label}
                </button>
              );
            })}
          </div>
        </Field>
      </div>
      <Field label="Geography as written" hint="States left out or allowed are read from here.">
        <input value={f.geography_text} onChange={set('geography_text')} readOnly={readOnly} placeholder={readOnly ? '' : 'e.g. United States (excluding Alaska and Hawaii) and Canada'} style={{
          width: '100%', padding: '6px 8px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 5,
          fontFamily: 'inherit', background: readOnly ? 'var(--surface-2)' : 'var(--surface)', color: 'var(--text)',
        }} />
      </Field>
      <Field label="Criteria">
        {ta('criteria_text', 2, 'e.g. $3M+ revenue')}
      </Field>
      <Field label="Tier signals" hint="One per line.">
        {RULE_TIERS.map(([t, label]) => (
          <div key={t} style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 3 }}>{label}</div>
            {ta(`sig_${t}`, Math.max(2, f[`sig_${t}`].split('\n').length + 1), '')}
          </div>
        ))}
      </Field>
      {rub && (
        <div style={{ marginTop: 4 }}>
          <button type="button" onClick={() => setShowRubric((v) => !v)} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
            {showRubric ? '▾' : '▸'} Valuation rubric · {rub.multiple_basis === 'revenue' ? 'revenue' : 'EBITDA'} multiple {rub.tiers && rub.tiers.low} / {rub.tiers && rub.tiers.average} / {rub.tiers && rub.tiers.high}
          </button>
          {showRubric && (
            <div style={{ marginTop: 8, fontSize: 11.5, overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr style={{ color: 'var(--text-4)', textAlign: 'left' }}>
                    <th style={{ padding: '4px 6px', fontWeight: 500 }}>Factor</th><th style={{ padding: '4px 6px', fontWeight: 500 }}>Low</th>
                    <th style={{ padding: '4px 6px', fontWeight: 500 }}>Average</th><th style={{ padding: '4px 6px', fontWeight: 500 }}>High</th>
                  </tr>
                </thead>
                <tbody>
                  {[['Multiple', rub.tiers], ['Revenue', rub.revenue_size], ['EBITDA', rub.ebitda_size]].filter(([, t]) => t && (t.low || t.average || t.high)).map(([name, t]) => (
                    <tr key={name} style={{ borderTop: '1px solid var(--border)', fontWeight: 600 }}>
                      <td style={{ padding: '4px 6px' }}>{name}</td><td style={{ padding: '4px 6px' }}>{t.low || '—'}</td><td style={{ padding: '4px 6px' }}>{t.average || '—'}</td><td style={{ padding: '4px 6px' }}>{t.high || '—'}</td>
                    </tr>
                  ))}
                  {(rub.rows || []).map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top' }}>
                      <td style={{ padding: '4px 6px', color: 'var(--text-2)', fontWeight: 500 }}>{r.factor}</td>
                      <td style={{ padding: '4px 6px', color: 'var(--text-3)' }}>{r.low || '—'}</td>
                      <td style={{ padding: '4px 6px', color: 'var(--text-3)' }}>{r.average || '—'}</td>
                      <td style={{ padding: '4px 6px', color: 'var(--text-3)' }}>{r.high || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(rub.notes || []).length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 16, color: 'var(--text-3)', lineHeight: 1.5 }}>
                  {rub.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
window.IndustryRulesBlock = IndustryRulesBlock;

// The industry's national consolidators — Find buyers' strategic discovery
// offers them on every search in the industry (their own tags rarely name the
// trade, so no keyword search finds them). One "Name, website" per line.
const IndustryStrategicsBlock = ({ industry }) => {
  const [saved, setSaved] = React.useState(undefined);
  const [txt, setTxt] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const asText = (items) => (items || []).map((x) => (x.domain ? `${x.name}, ${x.domain}` : x.name)).join('\n');
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.getIndustryStrategics(industry.id)
      .then((r) => { if (alive) { setSaved(asText(r.items)); setTxt(asText(r.items)); } })
      .catch(() => { if (alive) { setSaved(''); setTxt(''); } });
    return () => { alive = false; };
  }, [industry.id]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await window.AtlasApi.saveIndustryStrategics(industry.id, txt);
      setSaved(asText(r.items)); setTxt(asText(r.items));
      pushToast({ type: 'success', title: 'National strategics saved' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save', body: e.message });
    } finally { setBusy(false); }
  };
  if (saved === undefined) return null;
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}
          title="Offered by Find buyers' strategic discovery on every search in this industry">National strategics</span>
        <span style={{ flex: 1 }} />
        {txt !== saved && <Btn size="xs" variant="accent" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>}
      </div>
      <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={4} placeholder="APi Group, apigroup.com" style={{
        width: '100%', padding: '6px 8px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 5,
        fontFamily: 'inherit', resize: 'vertical', background: 'var(--surface)', color: 'var(--text)',
      }} />
    </div>
  );
};
window.IndustryStrategicsBlock = IndustryStrategicsBlock;

const IndustryDrawer = ({ industry, isNew, allMembers, onClose, onChange }) => {
  // A coverage transfer is not a normal save: re-deriving ownership for a big
  // industry touches tens of thousands of companies and queues one DealCloud
  // push each, so the request can run for the better part of a minute. With
  // no pending state the drawer just sat there looking inert — and an
  // impatient second click would start a second transfer of the same size.
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    name: industry?.name || '',
    assigned_user_id: industry?.assigned_user_id || '',
    icp_notes: industry?.icp_notes || '',
    active: industry?.active ?? true,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Aliases are not part of the form: each add/remove is its own immediate
  // API call (the server owns uniqueness — one alias maps to exactly one
  // industry). Seed from the row, update from each response, and tell the
  // list to reload so its Aliases column follows.
  const [aliases,   setAliases]   = React.useState(industry?.aliases || []);
  const [newAlias,  setNewAlias]  = React.useState('');
  const [aliasBusy, setAliasBusy] = React.useState(false);

  // Also drives the button label — a transfer deserves a different word from
  // a rename, because it is a different amount of work.
  const ownerChanged = !isNew
    && (industry?.assigned_user_id || null) !== (form.assigned_user_id || null);

  const save = async () => {
    const payload = {
      name: form.name.trim(),
      assigned_user_id: form.assigned_user_id || null,
      icp_notes: form.icp_notes.trim() || null,
      active: form.active,
    };
    // Reassigning the owner transfers the industry's book: every ongoing or
    // not-yet-engaged company follows the new lead (paused/won/lost deals
    // keep their original owner). Make that explicit before it happens.
    if (ownerChanged) {
      const newName = (allMembers.find((m) => m.id === form.assigned_user_id) || {}).full_name;
      const ok = await showConfirm({
        title: `Transfer ${industry.name} to ${newName || 'unassigned'}?`,
        body: 'All ongoing, on-hold and prospect companies in this industry move to the new owner, and the new owner is pushed to DealCloud. Won and lost deals stay with whoever ran them.',
        confirmLabel: 'Transfer',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await window.AtlasApi.createIndustry(payload);
        pushToast({ type: 'success', title: 'Industry created' });
      } else {
        const res = await window.AtlasApi.updateIndustry(industry.id, payload);
        const n = res && res.companies_transferred;
        const nc = res && res.contacts_transferred;
        const ne = res && res.engagements_transferred;
        const q = res && res.dc_pushes_queued;
        const parts = [];
        if (n > 0) parts.push(`${n} compan${n === 1 ? 'y' : 'ies'}`);
        if (nc > 0) parts.push(`${nc} contact${nc === 1 ? '' : 's'}`);
        if (ne > 0) parts.push(`${ne} engagement${ne === 1 ? '' : 's'}`);
        pushToast({
          type: 'success',
          title: 'Industry saved',
          body: parts.length
            ? `${parts.join(', ')} transferred to the new owner`
              + (q ? ` · ${q} queued for DealCloud.` : '.')
            : undefined,
        });
      }
      onChange();
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await showConfirm({
      title: 'Deactivate this industry?',
      body: 'Companies tagged with it stay tagged; the industry just won\'t show in pickers.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!ok) return;
    await window.AtlasApi.deleteIndustry(industry.id);
    pushToast({ type: 'success', title: 'Industry deactivated' });
    onChange();
    onClose();
  };

  const sortAliases = (list) =>
    [...list].sort((a, b) => a.alias.localeCompare(b.alias, undefined, { sensitivity: 'base' }));

  // apiFetch throws "<status> <statusText>: <json body>". For a 409 the
  // server's `detail` IS the message (why the alias was refused), so lift it
  // out of that wrapper instead of toasting raw JSON.
  const apiDetail = (e) => {
    const raw = (e && e.message) || '';
    const i = raw.indexOf('{');
    if (i !== -1) {
      try { const d = JSON.parse(raw.slice(i)); if (d && d.detail) return String(d.detail); } catch (_) { /* not JSON */ }
    }
    return raw || 'Something went wrong';
  };

  const addAlias = async () => {
    const alias = newAlias.trim();
    if (!alias || aliasBusy) return;
    setAliasBusy(true);
    try {
      const created = await window.AtlasApi.addIndustryAlias(industry.id, alias);
      setAliases((list) => sortAliases([...list, created]));
      setNewAlias('');
      pushToast({ type: 'success', title: 'Alias added', body: `"${alias}" now maps to ${industry.name}.` });
      onChange();
    } catch (e) {
      // A 409's detail says why: the text is an industry's own name, or
      // another industry already owns the alias. Toast that sentence.
      pushToast({ type: 'error', title: 'Could not add alias', body: apiDetail(e) });
    } finally {
      setAliasBusy(false);
    }
  };

  const removeAlias = async (a) => {
    try {
      await window.AtlasApi.deleteIndustryAlias(industry.id, a.id);
      setAliases((list) => list.filter((x) => x.id !== a.id));
      pushToast({ type: 'success', title: 'Alias removed' });
      onChange();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not remove alias', body: apiDetail(e) });
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={isNew ? 'New industry' : industry.name}
      subtitle={isNew ? undefined : `Owned by ${industry.assigned_user_name || '—'}`}
      footer={(
        <>
          {!isNew && <Btn size="sm" variant="ghost" onClick={remove} disabled={saving}>Deactivate</Btn>}
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={saving}>
            {saving
              ? (ownerChanged ? 'Transferring…' : 'Saving…')
              : (isNew ? 'Create' : 'Save')}
          </Btn>
        </>
      )}
    >
      <Field label="Name"><TextInput value={form.name} onChange={set('name')} /></Field>
      <Field label="Owner" hint="The coverage lead — owns every ongoing and prospect company in this industry. Changing it transfers that book; paused, won, and lost deals stay with whoever ran them.">
        <Select
          value={form.assigned_user_id || ''}
          onChange={set('assigned_user_id')}
          options={[
            { value: '', label: '(unassigned)' },
            ...allMembers.filter((u) => u.active !== false)
              .map((u) => ({ value: u.id, label: `${u.full_name} (${(u.role || '').replace(/_/g, ' ')})` })),
          ]}
        />
      </Field>
      <Field label="ICP notes" hint="Free text — minimum size, geography, deal flavor.">
        <textarea value={form.icp_notes} onChange={set('icp_notes')} rows={4} style={{
          width: '100%', padding: '6px 8px', fontSize: 12.5,
          border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'inherit',
          resize: 'vertical', minHeight: 60,
        }} />
      </Field>
      {!isNew && window.IndustryRulesBlock && <window.IndustryRulesBlock industry={industry} />}
      {!isNew && window.IndustryStrategicsBlock && <window.IndustryStrategicsBlock industry={industry} />}
      {!isNew && (
        // Not a <Field>: that is a <label>, and a label with no `for` adopts
        // its first labelable descendant — here the first pill's × button —
        // so a click on the caption, the hint, or any pill's text would fire
        // a delete. Same caption/hint markup as Field, on a plain div.
        <div style={{ display: 'block', marginBottom: 12 }}>
          <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>Aliases</div>
          {aliases.length
            ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                {aliases.map((a) => (
                  <Pill key={a.id} hue="slate" sz="sm">
                    {a.alias}
                    <button type="button" onClick={() => removeAlias(a)} title="Remove alias" style={{
                      background: 'transparent', border: 'none', padding: 0, marginLeft: 2,
                      cursor: 'pointer', color: 'inherit', opacity: 0.65, fontSize: 13, lineHeight: 1,
                    }}>×</button>
                  </Pill>
                ))}
              </div>
            )
            : <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginBottom: 8 }}>No aliases yet — only the industry's own name matches.</div>}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <TextInput
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }}
              placeholder="Another name DealCloud uses…"
              disabled={aliasBusy}
            />
            <Btn size="xs" variant="outline" onClick={addAlias} disabled={aliasBusy || !newAlias.trim()}>Add</Btn>
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 3 }}>
            Other names DealCloud uses for this industry — the sync and the backfill map them here. A company's own DealCloud industry always wins over an alias.
          </div>
        </div>
      )}
    </Drawer>
  );
};

// ============ FIELDS ============

const FILL_MODE_HUE = {
  manual:     'slate',
  imported:   'teal',
  web_search: 'violet',
  ai_prompt:  'amber',
};

const OBJECT_TYPES = [
  { value: 'company',    label: 'Companies' },
  { value: 'contact',    label: 'Contacts' },
  { value: 'engagement', label: 'Engagements' },
];

const FieldsSettings = () => {
  const [defs, setDefs] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [filter, setFilter] = React.useState('company');

  const reload = React.useCallback(async () => {
    const all = await Promise.all(
      OBJECT_TYPES.map(({ value }) => window.AtlasData.loadFieldDefinitions(value)),
    );
    setDefs(all.flat());
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  if (defs === null) {
    return <SettingsHeader title="Fields" sub="Loading…" />;
  }

  const visible = defs.filter((f) => f.object_type === filter);

  return (
    <>
      <SettingsHeader
        title="Fields"
        action={(
          <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />} onClick={() => setCreating(true)}>
            New field
          </Btn>
        )}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: '0 28px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36 }}>
        {OBJECT_TYPES.map((o) => {
          const active = filter === o.value;
          const n = defs.filter((f) => f.object_type === o.value).length;
          return (
            <button key={o.value} onClick={() => setFilter(o.value)} style={{
              padding: '0 10px', height: 36, border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-3)',
              borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {o.label}
              <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{n}</span>
            </button>
          );
        })}
      </div>

      <div style={{ padding: '20px 28px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
          <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 2, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Field</div>
            <div style={{ width: 130, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Source</div>
            <div style={{ flex: 2, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Description</div>
            <div style={{ width: 80, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Trigger</div>
            <div style={{ width: 60 }} />
          </div>
          {visible.length === 0 && (
            <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)' }}>No fields defined.</div>
          )}
          {visible.map((f) => (
            <div key={f.id}
              onClick={() => setEditing(f)}
              style={{
                display: 'flex', minHeight: 44, alignItems: 'center', cursor: 'pointer',
                borderBottom: '1px solid var(--border)',
                opacity: f.active ? 1 : 0.5,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ flex: 2, padding: '8px 14px' }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{f.display_name}</div>
                <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{f.field_key}</div>
              </div>
              <div style={{ width: 130, padding: '0 10px' }}>
                <Pill hue={FILL_MODE_HUE[f.fill_mode] || 'slate'} sz="sm">
                  {f.fill_mode.replace('_', ' ')}
                </Pill>
              </div>
              <div style={{ flex: 2, padding: '0 10px', fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.description || '—'}
              </div>
              <div style={{ width: 80, padding: '0 10px', fontSize: 11, color: 'var(--text-3)' }}>
                {f.trigger_mode}
              </div>
              <div style={{ width: 60, display: 'flex', justifyContent: 'flex-end', paddingRight: 14 }}>
                <Icon.ChevronR size={12} color="var(--text-4)" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {(editing || creating) && (
        <FieldDefinitionDrawer
          field={editing}
          isNew={creating}
          objectType={filter}
          onClose={() => { setEditing(null); setCreating(false); }}
          onChange={async () => { await reload(); }}
        />
      )}
    </>
  );
};

// All field "kinds" surfaced in the picker. Each maps to a (field_type, fill_mode)
// tuple under the hood — most are stored as-is; web_search/ai_prompt/formula
// are special-cased.
const FIELD_KINDS = [
  { kind: 'text',        label: 'Text',        icon: 'Doc',      desc: 'Single-line text.' },
  { kind: 'number',      label: 'Number',      icon: 'Activity', desc: 'Whole or decimal numbers.' },
  { kind: 'currency',    label: 'Currency',    icon: 'Money',    desc: 'Monetary value with currency code.' },
  { kind: 'date',        label: 'Date',        icon: 'Calendar', desc: 'Calendar date.' },
  { kind: 'boolean',     label: 'Yes / no',    icon: 'Check',    desc: 'Toggle.' },
  { kind: 'select',      label: 'Select',      icon: 'Tag',      desc: 'Pick one from a list.' },
  { kind: 'multiselect', label: 'Multi-select',icon: 'Tag',      desc: 'Pick many from a list.' },
  { kind: 'user',        label: 'User',        icon: 'User',     desc: 'Reference to a team member.' },
  { kind: 'relation',    label: 'Relation',    icon: 'Diamond',  desc: 'Link to another record (company, contact, …).' },
  { kind: 'document',    label: 'Document',    icon: 'Doc',      desc: 'Long-form rich text.' },
  { kind: 'formula',     label: 'Formula',     icon: 'Lightning',desc: 'Computed from other fields. Read-only.' },
  { kind: 'web_search',  label: 'Web search',  icon: 'Globe',    desc: 'AI fetches the value via web search on demand.' },
  { kind: 'ai_prompt',   label: 'AI prompt',   icon: 'Sparkle',  desc: 'AI computes the value from existing record data.' },
];

const RELATION_TARGETS = [
  { value: 'company',    label: 'Companies' },
  { value: 'contact',    label: 'Contacts' },
  { value: 'engagement', label: 'Engagements' },
  { value: 'industry',   label: 'Industries' },
  { value: 'team',       label: 'Teams' },
];

// Pick the UI kind from the persisted (field_type, fill_mode) pair.
const kindOf = (f) => {
  if (!f) return 'text';
  if (f.fill_mode === 'web_search') return 'web_search';
  if (f.fill_mode === 'ai_prompt')  return 'ai_prompt';
  return f.field_type || 'text';
};

const FieldDefinitionDrawer = ({ field, isNew, objectType, onClose, onChange }) => {
  const [kind, setKind] = React.useState(() => kindOf(field));
  const [form, setForm] = React.useState({
    object_type:  field?.object_type  || objectType,
    field_key:    field?.field_key    || '',
    display_name: field?.display_name || '',
    description:  field?.description  || '',
    fill_prompt:  field?.fill_prompt  || '',
    fill_model:   field?.fill_model   || 'claude-haiku-4-5',
    trigger_mode: field?.trigger_mode || 'on_demand',
    active:       field?.active ?? true,
    field_config: field?.field_config || {},
  });
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);
  const [sampleId, setSampleId] = React.useState('');         // '' = auto-pick (smart)
  const [sampleOpts, setSampleOpts] = React.useState(null);   // records to choose from

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setConfig = (k, v) => setForm((f) => ({ ...f, field_config: { ...f.field_config, [k]: v } }));

  // Select/multiselect options textarea. Kept as local raw text (not derived
  // from field_config on every render) so typing a mid-line " — " separator
  // isn't eaten by the parse→re-render round trip. Each line is
  // "Option — description"; descriptions land in field_config.option_descriptions
  // ({option: description}) and surface as ⓘ hints wherever the list renders.
  const [optionsText, setOptionsText] = React.useState(() => {
    const descs = field?.field_config?.option_descriptions || {};
    return (field?.field_config?.options || [])
      .map((o) => (descs[o] ? `${o} — ${descs[o]}` : o)).join('\n');
  });
  const onOptionsText = (raw) => {
    setOptionsText(raw);
    const opts = [];
    const descs = {};
    raw.split('\n').forEach((line) => {
      const m = line.split(/\s+—\s+|\s+--\s+/);  // em dash or "--", spaced
      const label = (m[0] || '').trim();
      if (!label) return;
      opts.push(label);
      const d = m.slice(1).join(' — ').trim();
      if (d) descs[label] = d;
    });
    setForm((f) => ({ ...f, field_config: {
      ...f.field_config, options: opts,
      ...(Object.keys(descs).length ? { option_descriptions: descs } : { option_descriptions: undefined }),
    } }));
  };

  // Auto-slugify field_key from display_name while creating
  React.useEffect(() => {
    if (!isNew) return;
    const slug = (form.display_name || '').toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '_').replace(/^_+|_+$/g, '');
    setForm((f) => f.field_key === '' || f.field_key === slug ? { ...f, field_key: slug } : f);
  }, [form.display_name, isNew]);

  // Load sample records of the matching object_type so the user can pick a
  // specific record to test the formula / AI prompt against.
  React.useEffect(() => {
    const lister = form.object_type === 'company'    ? window.AtlasApi.listCompanies
                 : form.object_type === 'contact'    ? window.AtlasApi.listContacts
                 :                                     window.AtlasApi.listEngagements;
    lister({ limit: 50 }).then((res) => {
      const items = (res && res.items) || [];
      setSampleOpts(items.map((r) => ({
        id: r.id,
        label: r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.id,
      })));
    }).catch(() => setSampleOpts([]));
  }, [form.object_type]);

  const save = async () => {
    setSaving(true);
    // Map kind → (field_type, fill_mode)
    const isAI = kind === 'web_search' || kind === 'ai_prompt';
    const payload = {
      ...form,
      field_type: kind === 'formula' ? 'formula'
                : isAI               ? (form.field_config.result_type || 'text')
                : kind,
      fill_mode:  isAI ? kind
                : kind === 'formula' ? 'manual'
                : 'manual',
    };
    try {
      if (isNew) await window.AtlasApi.createFieldDefinition(payload);
      else       await window.AtlasApi.updateFieldDefinition(field.id, payload);
      onChange();
      onClose();
      pushToast({ type: 'success', title: isNew ? 'Field created' : 'Field saved' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await showConfirm({
      title: 'Delete this field?',
      body: 'Existing column data is preserved — only this schema-level config goes away.',
      confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    await window.AtlasApi.deleteFieldDefinition(field.id);
    pushToast({ type: 'success', title: 'Field deleted' });
    onChange(); onClose();
  };

  const runTest = async () => {
    if (!field) { pushToast({ type: 'warning', title: 'Save first', body: 'Save the field, then test.' }); return; }
    if (!['web_search', 'ai_prompt'].includes(kind)) {
      pushToast({ type: 'warning', title: 'Not testable', body: 'Test fill is only available for web_search or ai_prompt.' });
      return;
    }
    setTesting(true); setTestResult(null);
    try {
      // Resolve the sample: explicit pick > first record from the list
      let recordId = sampleId;
      let sampleName = null;
      if (recordId) {
        sampleName = (sampleOpts || []).find((o) => o.id === recordId)?.label || recordId;
      } else {
        const lister = field.object_type === 'company'    ? window.AtlasApi.listCompanies
                     : field.object_type === 'contact'    ? window.AtlasApi.listContacts
                     :                                     window.AtlasApi.listEngagements;
        const records = (await lister({ limit: 1 })).items;
        if (!records.length) throw new Error('No records to test against');
        recordId = records[0].id;
        sampleName = records[0].name || records[0].full_name || records[0].id;
      }
      const result = await window.AtlasApi.fillField(field.object_type, recordId, field.field_key);
      setTestResult({ ok: true, sample_name: sampleName, ...result });
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
    } finally { setTesting(false); }
  };

  const showPromptArea = kind === 'web_search' || kind === 'ai_prompt';
  const cfg = form.field_config || {};

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={isNew ? 'New field' : form.display_name || field.field_key}
      subtitle={`On ${form.object_type}.${form.field_key || '—'}`}
      width={580}
      footer={(
        <>
          {!isNew && <Btn size="sm" variant="ghost" onClick={remove} disabled={saving}>Delete</Btn>}
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={saving || !form.display_name.trim()}>
            {saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}
          </Btn>
        </>
      )}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Display name"><TextInput value={form.display_name} onChange={set('display_name')} placeholder="e.g. Headcount" /></Field>
        <Field label="Field key" hint={isNew ? 'Column name (auto-derived)' : 'Read-only after creation'}>
          <TextInput value={form.field_key} onChange={set('field_key')} disabled={!isNew} />
        </Field>
      </div>

      <Field label="Type">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {FIELD_KINDS.map((k) => {
            const Ic = Icon[k.icon] || Icon.Doc;
            const a = kind === k.kind;
            return (
              <button key={k.kind} type="button" onClick={() => setKind(k.kind)} title={k.desc} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 6,
                border: a ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: a ? 'var(--accent-soft)' : 'var(--surface)',
                color: a ? 'var(--accent-text)' : 'var(--text-2)',
                cursor: 'pointer', fontSize: 11.5, fontWeight: a ? 600 : 500,
              }}>
                <Ic size={11} />{k.label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 6 }}>
          {FIELD_KINDS.find((k) => k.kind === kind)?.desc}
        </div>
      </Field>

      {/* ── Type-specific config ─────────────────────────────────────────── */}

      {kind === 'number' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Decimal places">
            <TextInput type="number" min="0" max="10" value={cfg.decimals ?? 0} onChange={(e) => setConfig('decimals', Number(e.target.value))} />
          </Field>
          <Field label="Suffix" hint='Shown after the value, e.g. "x" or "%"'>
            <TextInput value={cfg.suffix || ''} onChange={(e) => setConfig('suffix', e.target.value)} />
          </Field>
        </div>
      )}

      {kind === 'currency' && (
        <Field label="Currency code">
          <Combobox value={cfg.currency_code || 'USD'} onChange={(e) => setConfig('currency_code', e.target.value)}
            options={['USD','EUR','GBP','CAD','AUD','JPY','CHF','MXN'].map((c) => ({ value: c, label: c }))} />
        </Field>
      )}

      {(kind === 'select' || kind === 'multiselect') && (
        <Field label="Options" hint={'One per line. Optionally add a hover description after " — " (dash works too: "Option -- description").'}>
          <textarea
            value={optionsText}
            onChange={(e) => onOptionsText(e.target.value)}
            rows={5}
            placeholder={"Option A\nOption B — what picking B means\nOption C"}
            style={{
              width: '100%', padding: '8px 10px', fontSize: 12,
              border: '1px solid var(--border)', borderRadius: 5,
              fontFamily: 'inherit', resize: 'vertical', minHeight: 90,
            }}
          />
          <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 4 }}>
            {(cfg.options || []).length} option{(cfg.options || []).length === 1 ? '' : 's'}
          </div>
        </Field>
      )}

      {kind === 'relation' && (
        <Field label="Linked to" hint="Picking a record of this type fills the field.">
          <Combobox value={cfg.target_object || 'company'} onChange={(e) => setConfig('target_object', e.target.value)}
            options={RELATION_TARGETS} />
        </Field>
      )}

      {/* ── Test against (formula + AI fills both use the same sample picker) ─ */}
      {(kind === 'formula' || showPromptArea) && sampleOpts && sampleOpts.length > 0 && (
        <Field label="Test against" hint="Pick a record to preview/test, or let Atlas auto-pick one with non-null values">
          <Combobox
            value={sampleId}
            onChange={(e) => setSampleId(e.target.value)}
            options={[
              { value: '', label: '◇ Auto-pick (smart)' },
              ...sampleOpts.map((o) => ({ value: o.id, label: o.label })),
            ]}
          />
        </Field>
      )}

      {kind === 'formula' && (
        <FormulaEditor
          objectType={form.object_type}
          formula={cfg.formula || ''}
          sampleId={sampleId}
          onChange={(v) => setConfig('formula', v)}
        />
      )}

      {showPromptArea && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Returns" hint="What kind of value the AI should produce.">
              <Combobox
                value={cfg.result_type || 'text'}
                onChange={(e) => setConfig('result_type', e.target.value)}
                options={[
                  { value: 'text',    label: 'Text' },
                  { value: 'number',  label: 'Number' },
                  { value: 'date',    label: 'Date' },
                  { value: 'boolean', label: 'Yes / no' },
                ]}
              />
            </Field>
            <Field label="Model">
              <Combobox value={form.fill_model || 'claude-haiku-4-5'} onChange={set('fill_model')}
                options={[
                  { value: 'claude-haiku-4-5',  label: 'Haiku 4.5 (fast)' },
                  { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6 (balanced)' },
                ]} />
            </Field>
          </div>
          {kind === 'web_search' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {/* Where the web search runs. Anthropic's web_search tool is the
                  rate-limited default; Firecrawl asks the company's own site
                  first (question format), then searches with highlights and
                  reads the excerpts — no Anthropic search calls at all. */}
              <Field label="Search provider" hint="Firecrawl runs off Anthropic's rate limit; bulk fills go faster.">
                <Combobox
                  value={cfg.search_provider || 'anthropic'}
                  onChange={(e) => setConfig('search_provider', e.target.value === 'anthropic' ? undefined : e.target.value)}
                  options={[
                    { value: 'anthropic', label: 'Anthropic web search' },
                    { value: 'firecrawl', label: 'Firecrawl (site question → search)' },
                  ]}
                />
              </Field>
              {cfg.search_provider === 'firecrawl' && (
                <Field label="Search query" hint="Optional template; default is name + location + field name.">
                  <input value={cfg.search_query || ''} onChange={(e) => setConfig('search_query', e.target.value || undefined)}
                    placeholder="{{company.name}} {{company.hq_state}} employees"
                    style={{ width: '100%', padding: '6px 8px', fontSize: 11.5, fontFamily: 'var(--font-mono)',
                      border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface-2)' }} />
                </Field>
              )}
            </div>
          )}
          <Field
            label="Prompt template"
            hint='Use {{company.name}}, {{company.website}}, etc. Reply with just the value.'
          >
            <textarea value={form.fill_prompt} onChange={set('fill_prompt')} rows={6} style={{
              width: '100%', padding: '8px 10px', fontSize: 11.5,
              border: '1px solid var(--border)', borderRadius: 5,
              fontFamily: 'var(--font-mono)', resize: 'vertical', minHeight: 100,
              background: 'var(--surface-2)',
            }} />
          </Field>
        </>
      )}

      <Field label="Description" hint="Help text shown to teammates editing this field.">
        <textarea value={form.description} onChange={set('description')} rows={2} style={{
          width: '100%', padding: '6px 8px', fontSize: 12.5,
          border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'inherit', resize: 'vertical',
        }} />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Trigger" hint={showPromptArea ? "When to auto-fill" : ""}>
          <Combobox value={form.trigger_mode} onChange={set('trigger_mode')}
            options={[
              { value: 'on_demand', label: 'On demand' },
              { value: 'on_create', label: 'On create' },
              { value: 'never',     label: 'Never' },
            ]} />
        </Field>
        <Field label="Active">
          <Combobox value={String(form.active)} onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === 'true' }))}
            options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} />
        </Field>
      </div>

      {!isNew && showPromptArea && (
        <>
          <div style={{ marginTop: 14, padding: 12, background: 'var(--surface-2)', borderRadius: 7, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Test fill</span>
              <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>against a sample record</span>
              <div style={{ flex: 1 }} />
              <Btn size="xs" variant="outline" onClick={runTest} disabled={testing}>
                {testing ? 'Running…' : 'Run test'}
              </Btn>
            </div>
            {testResult && testResult.ok && (
              <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--text-2)' }}>
                <div><b>Sample:</b> {testResult.sample_name}</div>
                <div><b>Value:</b> {testResult.value || <em style={{ color: 'var(--text-4)' }}>null</em>}</div>
                <div style={{ color: 'var(--text-3)' }}>
                  cost ${testResult.cost_usd?.toFixed(4)} · {testResult.duration_ms}ms · {testResult.model}
                </div>
              </div>
            )}
            {testResult && !testResult.ok && (
              <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--rose-fg)' }}>
                {testResult.error}
              </div>
            )}
          </div>

          <BulkFillPanel field={field} />
        </>
      )}
    </Drawer>
  );
};

// Formula editor with live preview. Debounced 400ms; uses the parent drawer's
// chosen sample record when present, falls back to a smart auto-pick.
const FormulaEditor = ({ objectType, formula, sampleId, onChange }) => {
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!formula || !formula.trim()) { setPreview(null); return; }
    const handle = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await window.AtlasApi.previewFormula({
          object_type: objectType, formula,
          ...(sampleId ? { sample_id: sampleId } : {}),
        });
        setPreview(r);
      } catch (e) {
        setPreview({ ok: false, error: e.message });
      } finally {
        setBusy(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [formula, objectType, sampleId]);

  return (
    <Field label="Formula" hint="Reference other fields with {{field_key}}. Example: {{revenue_est}} - {{ebitda_est}}">
      <textarea
        value={formula}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        placeholder="{{revenue_est}} * 0.15"
        style={{
          width: '100%', padding: '8px 10px', fontSize: 11.5,
          border: '1px solid var(--border)', borderRadius: 5,
          fontFamily: 'var(--font-mono)', resize: 'vertical', minHeight: 90,
          background: 'var(--surface-2)',
        }}
      />
      <div style={{
        marginTop: 6, padding: '6px 10px', borderRadius: 5,
        background: preview && preview.parsed_ok ? 'var(--green-bg)' : 'var(--surface-2)',
        border: '1px solid var(--border)', fontSize: 11.5,
        color: preview && preview.parsed_ok ? 'var(--green-fg)' : 'var(--text-3)',
        fontFamily: 'var(--font-mono)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {busy && <span>Evaluating…</span>}
        {!busy && !formula?.trim() && <span style={{ color: 'var(--text-4)' }}>Type a formula to see a live preview</span>}
        {!busy && preview && preview.parsed_ok && (
          <>
            <span style={{ flex: 1 }}>
              On <b>{preview.sample_name}</b>: <b>{preview.value}</b>
            </span>
          </>
        )}
        {!busy && preview && !preview.parsed_ok && formula?.trim() && (
          <span style={{ color: 'var(--rose-fg)' }}>
            Returns null — check field references and arithmetic.
          </span>
        )}
      </div>
    </Field>
  );
};

// Bulk-fill panel: runs an AI-fill across many records.
// Two modes:
//   - foreground: synchronous batch (existing flow, instant feedback, blocks the API thread)
//   - background: creates a bulk_fill_job and polls progress (resumable, cancellable)
const BulkFillPanel = ({ field }) => {
  const [mode,       setMode]       = React.useState('background');
  const [maxRecords, setMaxRecords] = React.useState(25);
  const [costCap,    setCostCap]    = React.useState(2.0);
  const [skipFilled, setSkipFilled] = React.useState(true);

  // Foreground state
  const [busy,       setBusy]       = React.useState(false);
  const [last,       setLast]       = React.useState(null);
  const [running,    setRunning]    = React.useState(0);
  const [totalCost,  setTotalCost]  = React.useState(0);
  const [nextOffset, setNextOffset] = React.useState(0);

  // Background state
  const [job, setJob] = React.useState(null);
  // Set when a poll errors out — stops the loop without clearing the panel,
  // matching the old clearInterval-on-throw behaviour.
  const [jobLost, setJobLost] = React.useState(false);

  // Poll the running job, starting at 1.5s and backing off as it ages. The
  // hook stops on its own once the job leaves queued/running — the toast
  // fires from the poll that observed the flip.
  const jobId = job && job.id;
  const isJobLive = !!job && (job.status === 'queued' || job.status === 'running');
  window.usePolling(
    React.useCallback(async () => {
      try {
        const updated = await window.AtlasApi.getBulkFillJob(jobId);
        setJob(updated);
        if (updated.status === 'completed') {
          pushToast({ type: 'success', title: 'Job completed',
            body: `${updated.total_filled} filled · $${updated.total_cost_usd.toFixed(4)}` });
        } else if (updated.status === 'cancelled') {
          pushToast({ type: 'info', title: 'Job cancelled' });
        } else if (updated.status === 'failed') {
          pushToast({ type: 'error', title: 'Job failed',
            body: (updated.errors[0] || 'unknown').slice(0, 160) });
        }
        return updated.status;
      } catch (e) {
        // Same as the old loop: a lost job stops the poll rather than
        // retrying into the same error on every rung.
        setJobLost(true);
        pushToast({ type: 'error', title: 'Lost job', body: e.message });
        return null;
      }
    }, [jobId]),
    { active: isJobLive && !jobLost, key: jobId, first: 1500, signature: (status) => status },
  );

  const runForeground = async () => {
    setBusy(true);
    try {
      const result = await window.AtlasApi.bulkFillField(field.id, {
        max_records: maxRecords,
        cost_cap_usd: costCap,
        skip_already_filled: skipFilled,
        offset: nextOffset || 0,
      });
      setLast(result);
      setRunning((n) => n + (result.filled || 0));
      setTotalCost((c) => c + (result.total_cost_usd || 0));
      setNextOffset(result.next_offset);
      pushToast({
        type: result.failed > 0 ? 'warning' : 'success',
        title: `Filled ${result.filled} · skipped ${result.skipped}${result.failed ? ' · failed ' + result.failed : ''}`,
        body: `cost $${(result.total_cost_usd || 0).toFixed(4)}${result.cost_capped ? ' · cost cap hit' : ''}`,
      });
    } catch (e) {
      pushToast({ type: 'error', title: 'Bulk fill failed', body: e.message });
    } finally { setBusy(false); }
  };

  const startJob = async () => {
    try {
      const created = await window.AtlasApi.startBulkFillJob(field.id, {
        max_records: 0, // 0 = run until cap or out of records
        cost_cap_usd: costCap,
        skip_already_filled: skipFilled,
      });
      setJob(created);
      setJobLost(false);
      pushToast({ type: 'info', title: 'Job started',
        body: 'Runs in the background. Close the drawer if you want — progress keeps going.' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Failed to start', body: e.message });
    }
  };

  const cancelJob = async () => {
    if (!job) return;
    const ok = await showConfirm({
      title: 'Cancel job?',
      body: `Records already filled stay filled. The next pending record won't run.`,
      confirmLabel: 'Cancel job', danger: true,
    });
    if (!ok) return;
    const updated = await window.AtlasApi.cancelBulkFillJob(job.id);
    setJob(updated);
  };

  const resetForeground = () => { setLast(null); setRunning(0); setTotalCost(0); setNextOffset(0); };
  const resetJob = () => { setJob(null); setJobLost(false); };

  return (
    <div style={{ marginTop: 10, padding: 12, background: 'var(--surface-2)', borderRadius: 7, border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bulk fill</span>
        <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>across {field.object_type} records</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
          {[['background','Background'],['foreground','Foreground']].map(([k, label]) => (
            <button key={k} onClick={() => setMode(k)} disabled={isJobLive} style={{
              padding: '2px 8px', border: 'none', cursor: isJobLive ? 'not-allowed' : 'pointer',
              fontSize: 10.5, fontWeight: mode === k ? 600 : 500,
              background: mode === k ? 'var(--surface-2)' : 'transparent',
              color:      mode === k ? 'var(--text)'      : 'var(--text-3)',
              borderRadius: 4,
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* ── Background mode ───────────────────────────────────────────── */}
      {mode === 'background' && (
        <>
          {!job && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginBottom: 8 }}>
                <Field label="Cost cap (USD)" hint="Job stops once total spend reaches this">
                  <TextInput type="number" min="0" step="0.5" value={costCap}
                    onChange={(e) => setCostCap(Math.max(0, Number(e.target.value) || 0))} />
                </Field>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <input type="checkbox" id="skip-filled-bg" checked={skipFilled} onChange={(e) => setSkipFilled(e.target.checked)} />
                <label htmlFor="skip-filled-bg" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
                  Skip records already filled
                </label>
              </div>
              <Btn size="sm" variant="accent" onClick={startJob} icon={<Icon.Lightning size={11} />}>
                Start background fill
              </Btn>
            </>
          )}

          {job && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Pill hue={
                  job.status === 'running'   ? 'blue' :
                  job.status === 'queued'    ? 'amber':
                  job.status === 'completed' ? 'green':
                  job.status === 'cancelled' ? 'slate':
                                              'rose'
                } dot sz="sm">{job.status}</Pill>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {new Date(job.started_at || job.created_at).toLocaleTimeString()}
                </span>
                <div style={{ flex: 1 }} />
                {isJobLive && <Btn size="xs" variant="outline" onClick={cancelJob}>Cancel</Btn>}
                {!isJobLive && <Btn size="xs" variant="ghost" onClick={resetJob}>Start new</Btn>}
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
                fontSize: 11.5, marginBottom: 8,
              }}>
                <Stat label="Filled"   value={job.total_filled} />
                <Stat label="Skipped"  value={job.total_skipped} />
                <Stat label="Failed"   value={job.total_failed} tone={job.total_failed > 0 ? 'rose' : null} />
                <Stat label="Cost"     value={`$${job.total_cost_usd.toFixed(4)}`} />
              </div>
              <div style={{ height: 5, background: 'var(--surface)', borderRadius: 999, overflow: 'hidden', border: '1px solid var(--border)' }}>
                <div style={{
                  width: `${Math.min(100, (job.total_cost_usd / Math.max(0.0001, job.cost_cap_usd)) * 100)}%`,
                  height: '100%',
                  background: job.status === 'completed' ? 'oklch(0.55 0.14 155)'
                            : job.status === 'cancelled' ? 'var(--text-4)'
                            : job.status === 'failed'    ? 'oklch(0.55 0.14 25)'
                            : 'var(--accent)',
                  transition: 'width .4s',
                }} />
              </div>
              <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--text-4)' }}>
                cost ${job.total_cost_usd.toFixed(4)} of ${job.cost_cap_usd.toFixed(2)} cap · offset {job.current_offset}
              </div>
              {job.errors.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--rose-fg)' }}>
                    {job.errors.length} error{job.errors.length === 1 ? '' : 's'}
                  </summary>
                  <div style={{ marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-3)' }}>
                    {job.errors.slice(0, 10).map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                </details>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Foreground mode ───────────────────────────────────────────── */}
      {mode === 'foreground' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <Field label="Batch size" hint="Records per call (max 200)">
              <TextInput type="number" min="1" max="200" value={maxRecords}
                onChange={(e) => setMaxRecords(Math.max(1, Math.min(200, Number(e.target.value) || 1)))} />
            </Field>
            <Field label="Cost cap (USD)" hint="Stop once spend hits this">
              <TextInput type="number" min="0" step="0.5" value={costCap}
                onChange={(e) => setCostCap(Math.max(0, Number(e.target.value) || 0))} />
            </Field>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <input type="checkbox" id="skip-filled-fg" checked={skipFilled} onChange={(e) => setSkipFilled(e.target.checked)} />
            <label htmlFor="skip-filled-fg" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
              Skip records already filled
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Btn size="sm" variant="accent" onClick={runForeground} disabled={busy}>
              {busy ? 'Running…' : nextOffset ? `Fill next ${maxRecords}` : `Fill ${maxRecords}`}
            </Btn>
            {(running > 0 || totalCost > 0) && (
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                session: <b style={{ color: 'var(--text-2)' }}>{running}</b> filled · ${totalCost.toFixed(4)}
                <button onClick={resetForeground} style={{ marginLeft: 8, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 10.5, color: 'var(--text-4)' }}>reset</button>
              </span>
            )}
          </div>
          {last && (
            <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.55 }}>
              <div>
                <b>{last.filled}</b> filled · <b>{last.skipped}</b> skipped · {last.failed > 0 && <b style={{ color: 'var(--rose-fg)' }}>{last.failed} failed · </b>}
                <span style={{ color: 'var(--text-3)' }}>cost ${last.total_cost_usd.toFixed(4)}</span>
              </div>
              {last.cost_capped && (
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--amber-fg)' }}>
                  Cost cap reached — bump the cap and run again to continue.
                </div>
              )}
              {last.next_offset != null && !last.cost_capped && (
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-4)' }}>
                  More records available — click <b>Fill next {maxRecords}</b> to continue.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone }) => (
  <div style={{
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
    padding: '6px 8px',
  }}>
    <div style={{ fontSize: 9.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
    <div className="num" style={{
      fontSize: 14, fontWeight: 600, marginTop: 2,
      color: tone === 'rose' ? 'var(--rose-fg)' : 'var(--text)',
    }}>{value}</div>
  </div>
);

// ============ BULK-FILL JOBS ============
// Lists every bulk_fill_job (queued/running/completed/cancelled/failed).
// Polls (adaptively) while any job is live, idles otherwise. Cancel buttons
// flip the job's status; the worker checks between records and stops.

const JOB_STATUS_HUE = {
  queued:    'amber',
  running:   'blue',
  completed: 'green',
  cancelled: 'slate',
  failed:    'rose',
};

const BulkFillJobsSettings = () => {
  const [jobs, setJobs] = React.useState(null);
  const [filter, setFilter] = React.useState('all'); // all | live | done

  const load = React.useCallback(async () => {
    try {
      const data = await window.AtlasApi.listBulkFillJobs();
      const rows = Array.isArray(data) ? data : [];
      setJobs(rows);
      return rows;
    } catch (e) {
      pushToast({ type: 'error', title: 'Failed to load jobs', body: e.message });
      setJobs([]);
      return [];
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Poll while any job is queued or running. Backs off as the batch ages and
  // sleeps while the tab is hidden; the signature is the id/status set, not
  // the whole payload, so a per-record progress counter doesn't hold the
  // ladder on its fastest rung.
  const anyLive = !!jobs && jobs.some((j) => j.status === 'queued' || j.status === 'running');
  window.usePolling(load, {
    active: anyLive,
    first: 2000,
    signature: (rows) => (rows || []).map((j) => `${j.id}:${j.status}`).join(','),
  });

  const cancel = async (jobId) => {
    const ok = await showConfirm({
      title: 'Cancel this job?',
      body: 'The worker stops on the next record. Already-filled records stay filled.',
      confirmLabel: 'Cancel job', danger: true,
    });
    if (!ok) return;
    await window.AtlasApi.cancelBulkFillJob(jobId);
    load();
  };

  if (jobs === null) return <SettingsHeader title="Bulk fills" sub="Loading…" />;

  const isLive = (j) => j.status === 'queued' || j.status === 'running';
  const isDone = (j) => !isLive(j);
  const filtered = filter === 'live' ? jobs.filter(isLive)
                 : filter === 'done' ? jobs.filter(isDone)
                 : jobs;
  const liveCount = jobs.filter(isLive).length;

  return (
    <>
      <SettingsHeader
        title="Bulk fills"
        sub={`${jobs.length} jobs total · ${liveCount} live${liveCount > 0 ? ' (auto-refreshing)' : ''}.`}
        action={<Btn size="sm" variant="outline" icon={<Icon.Refresh size={12} />} onClick={load}>Refresh</Btn>}
      />
      <div style={{ padding: '14px 28px 4px', display: 'flex', alignItems: 'center', gap: 1 }}>
        {[['all','All',jobs.length],['live','Live',liveCount],['done','Done',jobs.length - liveCount]].map(([k, label, n]) => {
          const a = filter === k;
          return (
            <button key={k} onClick={() => setFilter(k)} style={{
              padding: '0 10px', height: 28, border: 'none', background: 'transparent',
              fontSize: 12, fontWeight: a ? 600 : 500, cursor: 'pointer',
              color: a ? 'var(--text)' : 'var(--text-3)',
              borderBottom: a ? '2px solid var(--text)' : '2px solid transparent',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {label}<span style={{ fontSize: 10, color: 'var(--text-4)' }} className="num">{n}</span>
            </button>
          );
        })}
      </div>

      <div style={{ padding: '8px 28px 28px' }}>
        {filtered.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '20px 0' }}>
            No jobs in this view. Start one from Settings → Objects → pick a web-search or AI-prompt field → Bulk fill → Start background fill.
          </div>
        )}
        {filtered.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
            <div style={{ display: 'flex', height: 28, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1.6, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Field</div>
              <div style={{ width: 110, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Status</div>
              <div style={{ width: 200, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Progress</div>
              <div style={{ width: 130, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Stats</div>
              <div style={{ width: 100, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>Cost</div>
              <div style={{ width: 90, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Started</div>
              <div style={{ width: 80, padding: '0 10px', display: 'flex', alignItems: 'center' }} />
            </div>
            {filtered.map((j) => {
              const pct = Math.min(100, (j.total_cost_usd / Math.max(0.0001, j.cost_cap_usd)) * 100);
              const fillBg = j.status === 'completed' ? 'oklch(0.55 0.14 155)'
                           : j.status === 'cancelled' ? 'var(--text-4)'
                           : j.status === 'failed'    ? 'oklch(0.55 0.14 25)'
                           : 'var(--accent)';
              return (
                <div key={j.id} style={{
                  display: 'flex', minHeight: 44, alignItems: 'center',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ flex: 1.6, padding: '8px 14px' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
                      {j.field_display_name || j.field_key || '—'}
                    </div>
                    <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-4)' }}>
                      {j.object_type}.{j.field_key || '—'}
                    </div>
                  </div>
                  <div style={{ width: 110, padding: '0 10px' }}>
                    <Pill hue={JOB_STATUS_HUE[j.status] || 'slate'} dot sz="sm">{j.status}</Pill>
                  </div>
                  <div style={{ width: 200, padding: '0 10px' }}>
                    <div style={{ height: 5, background: 'var(--surface-2)', borderRadius: 999, overflow: 'hidden', border: '1px solid var(--border)' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: fillBg, transition: 'width .4s' }} />
                    </div>
                    <div style={{ marginTop: 3, fontSize: 10, color: 'var(--text-4)' }}>
                      offset {j.current_offset}
                    </div>
                  </div>
                  <div style={{ width: 130, padding: '0 10px', fontSize: 11.5, color: 'var(--text-2)' }}>
                    <b>{j.total_filled}</b> filled
                    {j.total_skipped > 0 && <span style={{ color: 'var(--text-3)' }}> · {j.total_skipped} skip</span>}
                    {j.total_failed > 0 && <span style={{ color: 'var(--rose-fg)' }}> · {j.total_failed} fail</span>}
                  </div>
                  <div style={{ width: 100, padding: '0 10px', display: 'flex', justifyContent: 'flex-end', fontSize: 12 }} className="num">
                    ${j.total_cost_usd.toFixed(4)}
                  </div>
                  <div style={{ width: 90, padding: '0 10px', fontSize: 11, color: 'var(--text-3)' }}>
                    {j.started_at ? new Date(j.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </div>
                  <div style={{ width: 80, padding: '0 10px', display: 'flex', justifyContent: 'flex-end' }}>
                    {(j.status === 'queued' || j.status === 'running') && (
                      <Btn size="xs" variant="outline" onClick={() => cancel(j.id)}>Cancel</Btn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

// ============ TEST RUNNER ============
// Pick a record, see every field's current value side-by-side, and fire
// individual fills (or all at once) against that record. Lets you sanity-check
// the full field set for one record before running a bulk job.

const TestRunnerSettings = () => {
  const [objectType, setObjectType] = React.useState('company');
  const [records,    setRecords]    = React.useState([]);
  const [recordId,   setRecordId]   = React.useState('');
  const [record,     setRecord]     = React.useState(null);
  const [defs,       setDefs]       = React.useState([]);
  const [runningKey, setRunningKey] = React.useState(null);
  const [results,    setResults]    = React.useState({}); // field_key → {value, cost_usd, ok}

  const lister = React.useMemo(() => (
    objectType === 'company'    ? window.AtlasApi.listCompanies
  : objectType === 'contact'    ? window.AtlasApi.listContacts
  :                              window.AtlasApi.listEngagements
  ), [objectType]);

  const detailFetcher = React.useMemo(() => (
    objectType === 'company'    ? window.AtlasApi.getCompany
  : objectType === 'contact'    ? window.AtlasApi.getContact
  :                              window.AtlasApi.getEngagement
  ), [objectType]);

  // Load record list + field defs whenever object_type changes.
  React.useEffect(() => {
    setRecordId(''); setRecord(null); setResults({});
    Promise.all([
      lister({ limit: 50 }),
      window.AtlasApi.listFieldDefinitions(objectType),
    ]).then(([list, d]) => {
      const items = (list && list.items) || [];
      setRecords(items.map((r) => ({
        id: r.id,
        label: r.name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.id,
      })));
      setDefs((d || []).filter((x) => x.active).sort((a, b) => a.display_order - b.display_order));
    });
  }, [objectType, lister]);

  // Load full record details when one is picked
  React.useEffect(() => {
    if (!recordId || !detailFetcher) { setRecord(null); return; }
    setResults({});
    detailFetcher(recordId).then(setRecord).catch(() => setRecord(null));
  }, [recordId, detailFetcher]);

  const runFill = async (def) => {
    setRunningKey(def.field_key);
    try {
      const out = await window.AtlasApi.fillField(objectType, recordId, def.field_key);
      setResults((r) => ({ ...r, [def.field_key]: { ok: true, ...out } }));
      const fresh = await detailFetcher(recordId);
      setRecord(fresh);
      pushToast({ type: 'success', title: `Filled ${def.display_name}`,
        body: `${out.value || 'null'} · $${out.cost_usd.toFixed(4)}` });
    } catch (e) {
      setResults((r) => ({ ...r, [def.field_key]: { ok: false, error: e.message } }));
      pushToast({ type: 'error', title: `Failed ${def.display_name}`, body: e.message });
    } finally { setRunningKey(null); }
  };

  const runAllAI = async () => {
    const aiFields = defs.filter((d) => ['web_search', 'ai_prompt'].includes(d.fill_mode));
    for (const def of aiFields) {
      await runFill(def);
    }
  };

  // Look up the current value of a field on the record.
  const valueFor = (def) => {
    if (!record) return null;
    if (def.field_type === 'formula') return (record.computed || {})[def.field_key];
    return record[def.field_key];
  };

  const aiFieldCount = defs.filter((d) => ['web_search', 'ai_prompt'].includes(d.fill_mode)).length;

  return (
    <>
      <SettingsHeader
        title="Test runner"
        action={recordId && aiFieldCount > 0 && (
          <Btn size="sm" variant="accent" icon={<Icon.Lightning size={12} />}
            onClick={runAllAI} disabled={Boolean(runningKey)}>
            Run all AI fields ({aiFieldCount})
          </Btn>
        )}
      />
      <div style={{ padding: '20px 28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, marginBottom: 18 }}>
          <Field label="Object">
            <Combobox value={objectType} onChange={(e) => setObjectType(e.target.value)}
              options={[
                { value: 'company',    label: 'Companies' },
                { value: 'contact',    label: 'Contacts' },
                { value: 'engagement', label: 'Engagements' },
              ]} />
          </Field>
          <Field label="Record" hint={records.length === 0 ? 'No records of this type yet' : `${records.length} loaded`}>
            <Combobox value={recordId} onChange={(e) => setRecordId(e.target.value)}
              options={[
                { value: '', label: records.length ? '— Pick a record —' : '— No records —' },
                ...records.map((r) => ({ value: r.id, label: r.label })),
              ]} />
          </Field>
        </div>

        {!recordId && (
          <div style={{
            background: 'var(--surface)', border: '1px dashed var(--border-strong)', borderRadius: 9,
            padding: '40px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12,
          }}>
            Pick a record above.
          </div>
        )}

        {recordId && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
            <div style={{ display: 'flex', height: 28, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1.4, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Field</div>
              <div style={{ width: 110, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Type</div>
              <div style={{ width: 110, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Source</div>
              <div style={{ flex: 1.6, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Current value</div>
              <div style={{ width: 110, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>Action</div>
            </div>
            {defs.length === 0 && (
              <div style={{ padding: '20px 14px', fontSize: 12, color: 'var(--text-3)' }}>
                No field definitions for this object type yet. Add some in Settings → Objects.
              </div>
            )}
            {defs.map((def) => {
              const v        = valueFor(def);
              const result   = results[def.field_key];
              const isAI     = ['web_search', 'ai_prompt'].includes(def.fill_mode);
              const isFormula= def.field_type === 'formula';
              const display  = (() => {
                if (result) return result.ok ? `${result.value || '—'} · just filled` : `error: ${result.error}`;
                if (v == null || v === '') return null;
                if (isFormula) return formatComputed(def, v);
                if (def.field_type === 'currency') return fmtMoney(Number(v));
                if (def.field_type === 'number')   return formatComputed(def, v);
                if (def.field_type === 'boolean')  return v ? '✓' : '—';
                return String(v);
              })();
              return (
                <div key={def.id} style={{
                  display: 'flex', minHeight: 40, alignItems: 'center',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ flex: 1.4, padding: '8px 14px' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{def.display_name}</div>
                    <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{def.field_key}</div>
                  </div>
                  <div style={{ width: 110, padding: '0 10px' }}>
                    <Pill hue="slate" sz="sm">{def.field_type}</Pill>
                  </div>
                  <div style={{ width: 110, padding: '0 10px' }}>
                    <Pill hue={FILL_MODE_HUE[def.fill_mode] || 'slate'} sz="sm">{def.fill_mode.replace('_', ' ')}</Pill>
                  </div>
                  <div style={{
                    flex: 1.6, padding: '0 10px', fontSize: 12,
                    color: display == null ? 'var(--text-4)' : (result && !result.ok ? 'var(--rose-fg)' : 'var(--text-2)'),
                    fontFamily: isFormula ? 'var(--font-mono)' : 'inherit',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }} title={display == null ? '' : String(display)}>
                    {display == null ? '—' : display}
                  </div>
                  <div style={{ width: 110, padding: '0 10px', display: 'flex', justifyContent: 'flex-end' }}>
                    {isAI && (
                      <Btn size="xs" variant="outline" disabled={Boolean(runningKey)}
                        onClick={() => runFill(def)} icon={<Icon.Lightning size={10} />}>
                        {runningKey === def.field_key ? 'Running…' : 'Run'}
                      </Btn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

// ============ DATA QUALITY ============
// Surfaces the cleanup_tasks, industry_mismatches, and tag_candidates that
// /api/data-quality/* already returns. Read-only for now — bulk-apply actions
// land when the underlying CRUD endpoints get UI wiring.

const DataQualitySettings = () => {
  const [summary,    setSummary]    = React.useState(null);
  const [mismatches, setMismatches] = React.useState(null);
  const [tags,       setTags]       = React.useState(null);
  const [tasks,      setTasks]      = React.useState(null);
  const [tab,        setTab]        = React.useState('summary');

  React.useEffect(() => {
    Promise.all([
      window.AtlasApi.dqSummary().catch(() => null),
      window.AtlasApi.dqIndustryMismatch(100).catch(() => []),
      window.AtlasApi.dqTagCandidates(2).catch(() => []),
      window.AtlasApi.dqCleanupTasks().catch(() => []),
    ]).then(([s, m, t, c]) => {
      setSummary(s);
      setMismatches(Array.isArray(m) ? m : []);
      setTags(Array.isArray(t) ? t : []);
      setTasks(Array.isArray(c) ? c : []);
    });
  }, []);

  if (summary === null) return <SettingsHeader title="Data quality" sub="Loading…" />;

  return (
    <>
      <SettingsHeader
        title="Data quality"
      />

      <div style={{ padding: '12px 28px 0', display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid var(--border)' }}>
        {[
          ['summary',    'Summary'],
          ['mismatches', `Industry mismatches · ${(mismatches || []).length}`],
          ['tags',       `Tag candidates · ${(tags || []).length}`],
          ['tasks',      `Cleanup tasks · ${(tasks || []).length}`],
        ].map(([k, label]) => {
          const a = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: '0 12px', height: 32, border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: a ? 600 : 500, cursor: 'pointer',
              color: a ? 'var(--text)' : 'var(--text-3)',
              borderBottom: a ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1,
            }}>{label}</button>
          );
        })}
      </div>

      <div style={{ padding: '20px 28px' }}>
        {tab === 'summary' && summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, maxWidth: 980 }}>
            {[
              { label: 'Total contacts',           value: summary.contacts_total },
              { label: 'Contacts with company',    value: summary.contacts_with_company },
              { label: 'Industry mismatch',        value: summary.contact_company_industry_mismatch_count, tone: 'rose' },
              { label: 'Contacts missing industry',value: summary.contacts_missing_industry, tone: 'amber' },
              { label: 'Companies missing industry',value:summary.companies_missing_industry, tone: 'amber' },
              { label: 'Distinct custom tags',     value: summary.distinct_custom_tags },
              { label: 'Low-signal tags',          value: summary.low_signal_custom_tags, tone: 'slate' },
            ].map((k) => (
              <div key={k.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, padding: 14 }}>
                <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{k.label}</div>
                <div className="num" style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: k.tone === 'rose' ? 'var(--rose-fg)' : k.tone === 'amber' ? 'var(--amber-fg)' : 'var(--text)' }}>
                  {(k.value ?? 0).toLocaleString()}
                </div>
              </div>
            ))}
            {summary.top_custom_tags && summary.top_custom_tags.length > 0 && (
              <div style={{ gridColumn: '1 / -1', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, padding: 14 }}>
                <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>Top custom tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {summary.top_custom_tags.slice(0, 30).map((t) => (
                    <Pill key={t.tag} hue="slate" sz="sm">
                      {t.tag} · {t.count}
                    </Pill>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'mismatches' && (
          mismatches.length === 0
            ? <EmptyState text="No industry mismatches between contacts and their companies." />
            : (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
                <div style={{ display: 'flex', height: 28, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 1.4, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Contact</div>
                  <div style={{ flex: 1.4, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Company</div>
                  <div style={{ flex: 1, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Contact industry</div>
                  <div style={{ flex: 1, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Company industry</div>
                </div>
                {mismatches.map((m) => (
                  <div key={m.contact_id} style={{ display: 'flex', minHeight: 36, alignItems: 'center', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                    <div style={{ flex: 1.4, padding: '6px 14px' }}>{m.contact_name}</div>
                    <div style={{ flex: 1.4, padding: '0 10px', color: 'var(--text-2)' }}>{m.company_name}</div>
                    <div style={{ flex: 1, padding: '0 10px' }}><Pill hue="rose" sz="sm">{m.contact_industry || '—'}</Pill></div>
                    <div style={{ flex: 1, padding: '0 10px' }}><Pill hue="teal" sz="sm">{m.company_industry || '—'}</Pill></div>
                  </div>
                ))}
              </div>
            )
        )}

        {tab === 'tags' && (
          tags.length === 0
            ? <EmptyState text="No tag candidates flagged yet." />
            : (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
                <div style={{ display: 'flex', height: 28, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 2, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Tag</div>
                  <div style={{ width: 90, padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>Count</div>
                  <div style={{ width: 130, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Decision</div>
                  <div style={{ flex: 1.5, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Canonical</div>
                </div>
                {tags.map((t) => (
                  <div key={t.tag} style={{ display: 'flex', minHeight: 36, alignItems: 'center', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                    <div style={{ flex: 2, padding: '6px 14px', fontWeight: 500 }}>{t.tag}</div>
                    <div style={{ width: 90, padding: '0 10px', textAlign: 'right' }} className="num">{t.count}</div>
                    <div style={{ width: 130, padding: '0 10px' }}>
                      {t.decision ? <Pill hue={t.decision === 'keep' ? 'green' : t.decision === 'drop' ? 'rose' : 'amber'} sz="sm">{t.decision}</Pill> : <span style={{ color: 'var(--text-4)' }}>—</span>}
                    </div>
                    <div style={{ flex: 1.5, padding: '0 10px', color: 'var(--text-3)' }}>{t.canonical_tag || '—'}</div>
                  </div>
                ))}
              </div>
            )
        )}

        {tab === 'tasks' && (
          tasks.length === 0
            ? <EmptyState text="No cleanup tasks in the queue." />
            : (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
                {tasks.map((t, i) => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: i < tasks.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <Pill hue={t.priority === 'high' ? 'rose' : t.priority === 'medium' ? 'amber' : 'slate'} sz="sm">{t.priority}</Pill>
                    <Pill hue={t.task_type === 'industry' ? 'teal' : t.task_type === 'tag' ? 'violet' : t.task_type === 'duplicate' ? 'amber' : 'slate'} sz="sm">{t.task_type}</Pill>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{t.title}</div>
                      {t.details && <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{t.details}</div>}
                    </div>
                    <Pill hue={t.status === 'open' ? 'amber' : t.status === 'resolved' ? 'green' : 'blue'} sz="sm">{t.status}</Pill>
                  </div>
                ))}
              </div>
            )
        )}
      </div>
    </>
  );
};

const EmptyState = ({ text }) => (
  <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: 12.5, color: 'var(--text-3)', background: 'var(--surface)', border: '1px dashed var(--border-strong)', borderRadius: 10 }}>
    {text}
  </div>
);

// ============ Custom tags (Settings → Custom tags) ============
//
// DealCloud sprawled into 40+ custom tag names — most are derived from
// data we already track (per-owner GPP cohorts, campaign labels, funnel
// states). scripts/classify_tags.py sorts each tag into a kind bucket
// (canonical / campaign / state / derived / archived). This page is the
// catalog: see the current state, override classifications, set categories,
// and point derived tags at their canonical.
//
// API: GET /api/tags, PATCH /api/tags/:id (see routes/tags.py).

const TAG_KIND_LABEL = {
  canonical: 'Canonical',
  campaign:  'Campaign',
  state:     'State',
  derived:   'Derived',
  archived:  'Archived',
};
const TAG_KIND_HUE = {
  canonical: 'green',
  campaign:  'teal',
  state:     'amber',
  derived:   'slate',
  archived:  'rose',
};
const TAG_KIND_HINT = {
  canonical: 'Real label. Surfaced on contact detail + filters.',
  campaign:  'Marketing campaign cohort. Used in campaign analytics; hidden from generic filters.',
  state:     'Workflow state (e.g. "Begin Email Sequences"). Should live in a structured column eventually.',
  derived:   'Implied combination of (canonical × some filter). Hidden from default UI.',
  archived:  'Junk / deprecated / one-off. Stays in the DB for audit but never surfaces.',
};

const TagsSettings = () => {
  const [tags, setTags] = React.useState(null);
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [kindFilter, setKindFilter] = React.useState('');
  const [q, setQ] = React.useState('');
  const [editing, setEditing] = React.useState(null);
  // Cleanup tooling — multi-select + AI suggestion state.
  // selected: Set<tag_id>. suggestions: Map<tag_id, {kind, category, reason, confidence}>.
  const [selected, setSelected] = React.useState(() => new Set());
  const [suggestions, setSuggestions] = React.useState({});
  const [suggestingId, setSuggestingId] = React.useState(null);
  const [longTailThreshold, setLongTailThreshold] = React.useState(10);
  const [bulkBusy, setBulkBusy] = React.useState(false);

  const reload = React.useCallback(() => {
    setTags(null);
    setSelected(new Set());
    setSuggestions({});
    const params = {};
    if (includeArchived) params.include_archived = true;
    if (kindFilter) params.kind = kindFilter;
    if (q.trim()) params.q = q.trim();
    window.AtlasApi.listTags(params)
      .then((r) => setTags(r?.items || []))
      .catch(() => setTags([]));
  }, [includeArchived, kindFilter, q]);
  React.useEffect(() => { reload(); }, [reload]);

  const kpis = React.useMemo(() => {
    const t = tags || [];
    const by = {};
    for (const r of t) by[r.kind] = (by[r.kind] || 0) + 1;
    return by;
  }, [tags]);

  // --- Selection helpers ---
  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    if (!tags || tags.length === 0) return;
    setSelected((prev) => {
      const allSelected = tags.every((t) => prev.has(t.id));
      if (allSelected) return new Set();
      const next = new Set(prev);
      for (const t of tags) next.add(t.id);
      return next;
    });
  };

  // --- Bulk-archive selected rows ---
  const bulkArchiveSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Archive ${selected.size} tag${selected.size === 1 ? '' : 's'}? They'll be hidden from default filters but kept in the DB.`)) return;
    setBulkBusy(true);
    try {
      const out = await window.AtlasApi.bulkUpdateTags({
        tag_ids: Array.from(selected),
        kind: 'archived',
      });
      pushToast({ type: 'success', title: `Archived ${out?.updated ?? selected.size} tag${(out?.updated ?? selected.size) === 1 ? '' : 's'}` });
      reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Bulk archive failed', body: e.message });
    } finally { setBulkBusy(false); }
  };

  // --- Archive every canonical tag with ≤ N contacts (long-tail sweep) ---
  const archiveLongTail = async () => {
    const n = Number(longTailThreshold);
    if (!Number.isFinite(n) || n < 0) {
      pushToast({ type: 'error', title: 'Enter a non-negative number' });
      return;
    }
    if (!window.confirm(`Archive every canonical tag with ≤ ${n} contacts? This skips any tag you've already classified manually.`)) return;
    setBulkBusy(true);
    try {
      const out = await window.AtlasApi.bulkUpdateTags({
        max_contacts: n,
        only_canonical: true,
        kind: 'archived',
      });
      pushToast({
        type: 'success',
        title: `Archived ${out?.updated ?? 0} long-tail tag${(out?.updated ?? 0) === 1 ? '' : 's'}`,
      });
      reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Sweep failed', body: e.message });
    } finally { setBulkBusy(false); }
  };

  // --- Per-row AI suggest (Haiku) ---
  const requestSuggestion = async (tag) => {
    setSuggestingId(tag.id);
    try {
      const s = await window.AtlasApi.suggestTagKind(tag.id);
      setSuggestions((prev) => ({ ...prev, [tag.id]: s }));
    } catch (e) {
      pushToast({ type: 'error', title: 'Suggest failed', body: e.message });
    } finally { setSuggestingId(null); }
  };
  const applySuggestion = async (tag) => {
    const s = suggestions[tag.id];
    if (!s) return;
    try {
      await window.AtlasApi.updateTag(tag.id, {
        kind: s.kind,
        category: s.category || null,
      });
      pushToast({ type: 'success', title: `Set ${tag.name} → ${s.kind}` });
      // Optimistic: drop the suggestion + reload list so the new kind shows.
      setSuggestions((prev) => { const n = { ...prev }; delete n[tag.id]; return n; });
      reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Apply failed', body: e.message });
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        Custom tags
      </h2>

      {/* KPI strip — clickable: filters by kind */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 14 }}>
        {['canonical', 'campaign', 'state', 'derived', 'archived'].map((k) => (
          <button key={k} onClick={() => setKindFilter(kindFilter === k ? '' : k)}
            style={{
              textAlign: 'left', background: kindFilter === k ? 'var(--accent-soft)' : 'var(--surface)',
              border: '1px solid', borderColor: kindFilter === k ? 'var(--accent-text)' : 'var(--border)',
              borderRadius: 6, padding: '8px 12px', cursor: 'pointer',
            }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                           letterSpacing: '0.05em', fontWeight: 500 }}>
              {TAG_KIND_LABEL[k]}
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>
              {tags === null ? '…' : (kpis[k] || 0)}
            </div>
          </button>
        ))}
      </div>

      {/* Search + archived toggle */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search tag name…"
          style={{
            flex: 1, padding: '5px 10px', fontSize: 12.5,
            border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'inherit',
          }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
          <input type="checkbox" checked={includeArchived}
                 onChange={(e) => setIncludeArchived(e.target.checked)} />
          Include archived
        </label>
        {(kindFilter || q || includeArchived) && (
          <Btn size="xs" variant="ghost"
               onClick={() => { setKindFilter(''); setQ(''); setIncludeArchived(false); }}>
            Clear
          </Btn>
        )}
      </div>

      {/* Cleanup tools — long-tail sweep + bulk-archive action bar */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10,
        padding: '8px 12px', background: 'var(--surface-2)',
        border: '1px solid var(--border)', borderRadius: 6, fontSize: 12,
      }}>
        <span style={{ color: 'var(--text-3)' }}>Archive every canonical tag with ≤</span>
        <input
          type="number" min={0} value={longTailThreshold}
          onChange={(e) => setLongTailThreshold(e.target.value)}
          style={{
            width: 56, padding: '3px 6px', fontSize: 12,
            border: '1px solid var(--border)', borderRadius: 4,
            background: 'var(--surface)', color: 'var(--text)', textAlign: 'right',
          }}
        />
        <span style={{ color: 'var(--text-3)' }}>contacts</span>
        <Btn size="xs" variant="ghost" onClick={archiveLongTail} disabled={bulkBusy}>
          {bulkBusy ? 'Working…' : 'Sweep'}
        </Btn>
        <span style={{ flex: 1 }} />
        {selected.size > 0 && (
          <>
            <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>
              {selected.size} selected
            </span>
            <Btn size="xs" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear selection
            </Btn>
            <Btn size="xs" variant="accent" onClick={bulkArchiveSelected} disabled={bulkBusy}>
              {bulkBusy ? 'Working…' : `Archive ${selected.size}`}
            </Btn>
          </>
        )}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                     borderRadius: 8, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '28px 2fr 110px 110px 1fr 70px 110px',
          gap: 10, padding: '8px 14px', background: 'var(--surface-2)',
          fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <input
              type="checkbox"
              aria-label="Select all visible"
              checked={!!tags && tags.length > 0 && tags.every((t) => selected.has(t.id))}
              ref={(el) => {
                if (!el || !tags) return;
                const some = tags.some((t) => selected.has(t.id));
                const all = tags.length > 0 && tags.every((t) => selected.has(t.id));
                el.indeterminate = some && !all;
              }}
              onChange={toggleAllVisible}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div>Tag</div>
          <div>Kind</div>
          <div>Category</div>
          <div>Implies / notes</div>
          <div style={{ textAlign: 'right' }}>Contacts</div>
          <div style={{ textAlign: 'right' }}>Suggest</div>
        </div>
        {tags === null && (
          <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
        )}
        {tags && tags.length === 0 && (
          <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>
            No tags match the current filter.
          </div>
        )}
        {tags && tags.map((t, i) => {
          const sugg = suggestions[t.id];
          const isChecked = selected.has(t.id);
          return (
            <React.Fragment key={t.id}>
              <div
                onClick={() => setEditing(t)}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = ''}
                style={{
                  display: 'grid', gridTemplateColumns: '28px 2fr 110px 110px 1fr 70px 110px',
                  gap: 10, padding: '10px 14px', alignItems: 'center',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  cursor: 'pointer',
                }}>
                <div onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleSelected(t.id)}
                    aria-label={`Select ${t.name}`}
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)',
                                 overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.name}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-4)',
                                 overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.slug}
                  </div>
                </div>
                <div><Pill hue={TAG_KIND_HUE[t.kind]} sz="sm">{TAG_KIND_LABEL[t.kind]}</Pill></div>
                <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{t.category || '—'}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)',
                               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.canonical_tag_name
                    ? <>→ {t.canonical_tag_name}</>
                    : (t.description || <span style={{ color: 'var(--text-4)' }}>—</span>)}
                </div>
                <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600,
                               color: t.contacts > 0 ? 'var(--text)' : 'var(--text-4)' }}
                     className="num">
                  {t.contacts}
                </div>
                <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                  <Btn size="xs" variant="ghost"
                       disabled={suggestingId === t.id}
                       onClick={() => requestSuggestion(t)}
                       title="Ask Claude Haiku to suggest a kind">
                    {suggestingId === t.id ? '…' : '✨ Suggest'}
                  </Btn>
                </div>
              </div>
              {sugg && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: '8px 14px 10px 50px', background: 'var(--accent-soft)',
                    borderTop: '1px solid var(--border)', fontSize: 11.5,
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  }}>
                  <span style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                                  letterSpacing: '0.05em', fontWeight: 500 }}>
                    Suggestion
                  </span>
                  <Pill hue={TAG_KIND_HUE[sugg.kind]} sz="sm">{TAG_KIND_LABEL[sugg.kind] || sugg.kind}</Pill>
                  {sugg.category && <Pill hue="slate" sz="sm">{sugg.category}</Pill>}
                  <Pill hue={sugg.confidence === 'high' ? 'emerald' : sugg.confidence === 'low' ? 'rose' : 'amber'} sz="sm">
                    {sugg.confidence}
                  </Pill>
                  <span style={{ color: 'var(--text-2)', flex: 1, minWidth: 200 }}>{sugg.reason}</span>
                  <Btn size="xs" variant="ghost"
                       onClick={() => setSuggestions((p) => { const n = { ...p }; delete n[t.id]; return n; })}>
                    Dismiss
                  </Btn>
                  <Btn size="xs" variant="accent" onClick={() => applySuggestion(t)}>
                    Apply
                  </Btn>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <TagEditDrawer
        tag={editing}
        allTags={tags || []}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </div>
  );
};

const TagEditDrawer = ({ tag, allTags, onClose, onSaved }) => {
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!tag) return;
    setForm({
      kind:             tag.kind,
      category:         tag.category || '',
      canonical_tag_id: tag.canonical_tag_id || '',
      description:      tag.description || '',
    });
  }, [tag]);

  if (!tag) return null;

  // Only canonical tags can be the "implies" target — never another derived.
  const canonicalCandidates = allTags
    .filter((t) => t.kind === 'canonical' && t.id !== tag.id)
    .map((t) => ({ value: t.id, label: `${t.name} (${t.contacts} contacts)` }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        kind:             form.kind,
        category:         form.category?.trim() || null,
        canonical_tag_id: form.kind === 'derived' ? (form.canonical_tag_id || null) : null,
        description:      form.description?.trim() || null,
      };
      await window.AtlasApi.updateTag(tag.id, patch);
      pushToast({ type: 'success', title: 'Saved' });
      onSaved && onSaved();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  return (
    <Drawer
      open={!!tag}
      onClose={onClose}
      title={tag.name}
      subtitle={`${tag.contacts} contact${tag.contacts === 1 ? '' : 's'} carry this tag · slug ${tag.slug}`}
      footer={(
        <>
          <Btn size="xs" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="xs" variant="accent" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
        </>
      )}
    >
      <Field label="Kind"
        hint={TAG_KIND_HINT[form.kind]}>
        <Combobox
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
          options={Object.entries(TAG_KIND_LABEL).map(([v, l]) => ({ value: v, label: l }))}
        />
      </Field>

      <Field label="Category"
        hint="Free-form bucket: 'gpp' / 'referral_partner' / 'campaign' / '3pl' / etc. Used by analytics queries.">
        <TextInput
          value={form.category}
          placeholder="gpp"
          onChange={(e) => setForm({ ...form, category: e.target.value })}
        />
      </Field>

      {form.kind === 'derived' && (
        <Field label="Canonical tag this implies"
          hint="The canonical tag this derived row really stands for. Future cleanup tools will use it to fold derived populations back into the canonical.">
          <Combobox
            value={form.canonical_tag_id}
            onChange={(e) => setForm({ ...form, canonical_tag_id: e.target.value })}
            options={[{ value: '', label: '— none —' }, ...canonicalCandidates]}
          />
        </Field>
      )}

      <Field label="Description"
        hint="One-line description of what this tag actually tracks. Future contributors will thank you.">
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={4}
          placeholder="What does this tag mean? When should it be applied?"
          style={{
            width: '100%', padding: '6px 8px', fontSize: 12.5,
            border: '1px solid var(--border)', borderRadius: 5,
            background: 'var(--surface)', color: 'var(--text)',
            fontFamily: 'inherit', resize: 'vertical',
          }}
        />
      </Field>

      <div style={{ marginTop: 18, padding: 10, background: 'var(--surface-2)',
                     borderRadius: 6, fontSize: 11, color: 'var(--text-3)' }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                       letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
          DC reference
        </div>
        <div>DC id: <code className="mono">{tag.dealcloud_id || '—'}</code></div>
        <div>slug: <code className="mono">{tag.slug}</code></div>
      </div>
    </Drawer>
  );
};


// ============ DEALCLOUD SYNC (write-back outbox) ============
// Monitor for the Atlas → DealCloud write-back queue (dc_outbox). Shows
// pending/failed pushes with errors; failed and dry-run rows can be re-queued.

const OUTBOX_STATUS_HUE = { pending: 'amber', in_flight: 'blue', synced: 'green', failed: 'rose', dry_run: 'violet' };

// ── Costs (admins only) ───────────────────────────────────────────────────
// What Atlas spends, by activity (GET /api/costs). Claude spend comes from
// the llm_usage ledger; the research wand's runs carry their own total
// (Claude + Firecrawl) and credits; each Prospecting batch carries its own
// running total. Andres 2026-10-07: run costs live here and nowhere else in
// the app (2026-10-08: the Prospecting page has no ceiling to set — its
// spend is watched here).
const COST_PERIODS = [7, 30, 90];
const COST_RUN_STATUS = { done: ['found', 'blue'], not_found: ['not found', 'slate'], failed: ['failed', 'rose'], running: ['running', 'amber'] };
const COST_RECORD_PATH = { company: 'companies', contact: 'people', engagement: 'deals' };
const usd = (n, d = 2) => `$${Number(n || 0).toFixed(d)}`;

const CostTile = ({ label, value, sub }) => (
  <div style={{ flex: '1 1 150px', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', minWidth: 0 }}>
    <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
    <div className="num" style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 1 }}>{sub}</div>}
  </div>
);

const CostTable = ({ cols, rows, empty }) => (
  rows.length === 0
    ? <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '10px 0' }}>{empty}</div>
    : (
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 10, padding: '6px 12px', fontSize: 11, fontWeight: 600, color: 'var(--text-4)',
          textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
          {cols.map((c) => <span key={c.key} style={{ width: c.w, flex: c.w ? 'none' : 1, textAlign: c.num ? 'right' : 'left' }}>{c.label}</span>)}
        </div>
        {rows.map((r, i) => (
          <div key={r._key || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', fontSize: 12.5,
            borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--border)' }}>
            {cols.map((c) => (
              <span key={c.key} className={c.num ? 'num' : undefined} style={{
                width: c.w, flex: c.w ? 'none' : 1, minWidth: 0, textAlign: c.num ? 'right' : 'left',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: c.dim ? 'var(--text-3)' : 'var(--text)',
              }}>{c.render ? c.render(r) : r[c.key]}</span>
            ))}
          </div>
        ))}
      </div>
    )
);

const CostsSettings = () => {
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState(null);

  const reload = React.useCallback(async () => {
    setData(null);
    try {
      setData(await window.AtlasApi.costs(days));
    } catch (e) {
      pushToast({ type: 'error', title: 'Failed to load costs', message: String(e?.message || e) });
      setData({ claude: { by_activity: [] }, research: { by_person: [], by_field: [], recent: [] }, prospecting: { recent: [] } });
    }
  }, [days]);
  React.useEffect(() => { reload(); }, [reload]);

  const claude = data?.claude || { by_activity: [] };
  const research = data?.research || { by_person: [], by_field: [], recent: [] };
  const pros = data?.prospecting || { recent: [] };
  const top = Math.max(0.0001, ...claude.by_activity.map((a) => a.usd));
  const h4 = (t) => <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '22px 0 8px' }}>{t}</h4>;
  const when = (iso) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Costs</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {COST_PERIODS.map((d) => (
            <Btn key={d} size="xs" active={days === d} onClick={() => setDays(d)}>{d} days</Btn>
          ))}
          <Btn size="xs" onClick={reload}>Refresh</Btn>
        </div>
      </div>

      {data == null && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>}
      {data != null && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <CostTile label="Claude" value={usd(claude.usd)}
              sub={`${(claude.calls || 0).toLocaleString()} calls${claude.since ? ` · since ${new Date(claude.since).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : ''}`} />
            <CostTile label="Research wand" value={usd(research.usd)}
              sub={`${research.runs || 0} runs · ${research.used || 0} used`} />
            <CostTile label="Firecrawl credits" value={(research.firecrawl_credits || 0).toLocaleString()} sub="research wand" />
            <CostTile label="Apollo credits" value={(research.apollo_credits || 0).toLocaleString()} sub="research wand" />
            {data.parsing && (
              <CostTile label="Reading files" value={usd(data.parsing.usd)}
                sub={`${(data.parsing.parsed || 0).toLocaleString()} read · ${(data.parsing.pages || 0).toLocaleString()} pages`} />
            )}
            <CostTile label="Prospecting" value={usd(pros.usd)} sub={`${pros.batches || 0} batches · ${(pros.leads || 0).toLocaleString()} leads`} />
          </div>

          {h4('Claude by activity')}
          <CostTable empty="No Claude calls in this period." rows={claude.by_activity.map((a) => ({ ...a, _key: a.activity }))} cols={[
            { key: 'label', label: 'Activity', render: (a) => <span title={a.activity}>{a.label}</span> },
            { key: 'bar', label: '', w: 160, render: (a) => (
              <span style={{ display: 'block', height: 6, borderRadius: 3, background: 'var(--surface-2)' }}>
                <span style={{ display: 'block', height: 6, borderRadius: 3, width: `${Math.max(2, (a.usd / top) * 100)}%`, background: 'var(--accent)' }} />
              </span>
            ) },
            { key: 'calls', label: 'Calls', w: 70, num: true, dim: true, render: (a) => a.calls.toLocaleString() },
            { key: 'usd', label: 'Cost', w: 80, num: true, render: (a) => usd(a.usd) },
          ]} />

          {h4('Research wand by person')}
          <CostTable empty="No research runs in this period." rows={research.by_person.map((p) => ({ ...p, _key: p.name }))} cols={[
            { key: 'name', label: 'Person' },
            { key: 'runs', label: 'Runs', w: 70, num: true, dim: true },
            { key: 'usd', label: 'Cost', w: 80, num: true, render: (p) => usd(p.usd) },
          ]} />

          {h4('Research wand by field')}
          <CostTable empty="No research runs in this period." rows={research.by_field.map((f) => ({ ...f, _key: `${f.object_type}.${f.field_key}` }))} cols={[
            { key: 'label', label: 'Field' },
            { key: 'object_type', label: 'Record', w: 90, dim: true },
            { key: 'runs', label: 'Runs', w: 70, num: true, dim: true },
            { key: 'usd', label: 'Cost', w: 80, num: true, render: (f) => usd(f.usd) },
          ]} />

          {h4('Prospecting batches')}
          <CostTable empty="No prospecting runs in this period." rows={pros.recent.map((b) => ({ ...b, _key: b.id }))} cols={[
            { key: 'last_run', label: 'Last run', w: 120, dim: true, render: (b) => when(b.last_run) },
            { key: 'created_by', label: 'Created by', w: 130, render: (b) => b.created_by || '—' },
            { key: 'name', label: 'Batch', render: (b) => <a href={`#/prospecting?batch=${b.id}`} style={{ color: 'var(--accent-text)' }}>{b.name}</a> },
            { key: 'leads', label: 'Leads', w: 70, num: true, dim: true },
            { key: 'per', label: 'Per lead', w: 80, num: true, dim: true, render: (b) => (b.leads ? usd(b.usd / b.leads, 3) : '—') },
            { key: 'usd', label: 'Cost', w: 80, num: true, render: (b) => usd(b.usd) },
          ]} />

          {h4('Recent research runs')}
          <CostTable empty="No research runs in this period." rows={research.recent.map((r) => ({ ...r, _key: r.id }))} cols={[
            { key: 'created_at', label: 'When', w: 120, dim: true, render: (r) => when(r.created_at) },
            { key: 'requested_by', label: 'Who', w: 130, render: (r) => r.requested_by || '—' },
            { key: 'record_label', label: 'Record', render: (r) => (
              <a href={`#/${COST_RECORD_PATH[r.object_type]}/${r.record_id}`} style={{ color: 'var(--accent-text)' }}>{r.record_label}</a>
            ) },
            { key: 'label', label: 'Field', w: 130 },
            { key: 'status', label: 'Result', w: 90, render: (r) => {
              const [txt, hue] = r.used ? ['used', 'green'] : (COST_RUN_STATUS[r.status] || [r.status, 'slate']);
              return <Pill hue={hue} sz="sm">{txt}</Pill>;
            } },
            { key: 'credits', label: 'Credits', w: 90, num: true, dim: true, render: (r) =>
              [r.firecrawl_credits ? `${r.firecrawl_credits} FC` : null, r.apollo_credits ? `${r.apollo_credits} AP` : null].filter(Boolean).join(' · ') || '—' },
            { key: 'duration_ms', label: 'Time', w: 56, num: true, dim: true, render: (r) => (r.duration_ms ? `${Math.round(r.duration_ms / 1000)}s` : '—') },
            { key: 'usd', label: 'Cost', w: 70, num: true, render: (r) => usd(r.usd, 3) },
          ]} />
        </>
      )}
    </div>
  );
};

const DcSyncSettings = () => {
  const [data, setData] = React.useState(null);
  const [filter, setFilter] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);

  const reload = React.useCallback(async () => {
    try {
      setData(await window.AtlasApi.listDcOutbox(filter || undefined));
    } catch (e) {
      pushToast({ type: 'error', title: 'Failed to load outbox', message: String(e?.message || e) });
      setData({ items: [], counts: {} });
    }
  }, [filter]);

  React.useEffect(() => { reload(); }, [reload]);

  const retry = async (id) => {
    setBusyId(id);
    try {
      await window.AtlasApi.retryDcOutbox(id);
      pushToast({ type: 'success', title: 'Re-queued' });
      await reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Retry failed', message: String(e?.message || e) });
    } finally {
      setBusyId(null);
    }
  };

  const counts = data?.counts || {};
  const items = data?.items || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>DealCloud sync</h3>
        </div>
        <Btn size="sm" onClick={reload}>Refresh</Btn>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {['', 'pending', 'failed', 'synced', 'dry_run'].map((st) => (
          <Btn key={st || 'all'} size="xs" active={filter === st} onClick={() => setFilter(st)}>
            {st === '' ? 'All' : st}{st && counts[st] != null ? ` (${counts[st]})` : ''}
          </Btn>
        ))}
      </div>

      {data == null && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>}
      {data != null && items.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '18px 0' }}>
          Nothing in the outbox{filter ? ` with status "${filter}"` : ''}.
        </div>
      )}
      {items.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {items.map((r, i) => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', fontSize: 12.5,
              borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--border)',
            }}>
              <Pill hue={OUTBOX_STATUS_HUE[r.status] || 'slate'} sz="sm">{r.status}</Pill>
              <span style={{ color: 'var(--text-3)', width: 66, flexShrink: 0 }}>{r.entity_type}</span>
              <span style={{ color: 'var(--text)', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.entity_label || r.entity_id}
              </span>
              {r.dc_entry_id && <code className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>DC {r.dc_entry_id}</code>}
              {r.last_error && (
                <span title={r.last_error} style={{ color: 'var(--rose-fg)', fontSize: 11.5, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.last_error}
                </span>
              )}
              <span style={{ color: 'var(--text-4)', fontSize: 11, flexShrink: 0 }}>
                {r.attempts > 0 ? `${r.attempts}× · ` : ''}{new Date(r.updated_at || r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
              {(r.status === 'failed' || r.status === 'dry_run') && (
                <Btn size="xs" disabled={busyId === r.id} onClick={() => retry(r.id)}>
                  {busyId === r.id ? 'Queuing…' : 'Retry'}
                </Btn>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ PERMISSIONS ============
// Read-only view of the backend capability registry (app/permissions.py via
// GET /api/auth/permissions). Because the endpoint serializes the same
// registry the route guards enforce, this pane can't drift from reality.

const TIER_HUE  = { admin: 'amber', editor: 'blue', viewer: 'slate' };
const TIER_NAME = { admin: 'Admin', editor: 'Member', viewer: 'Viewer' };

// What each team role sees — the pages in the sidebar (and the routes and
// search behind them). A role left with only standalone pages (Calling) is
// also locked out of CRM data by the API; see backend app/page_access.py.
const RolePagesMatrix = ({ canEdit }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  React.useEffect(() => {
    window.AtlasApi.rolePages().then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <div style={{ fontSize: 12.5, color: 'var(--rose-fg)', marginBottom: 20 }}>Couldn't load role pages: {err}</div>;
  if (!data) return null;
  const roles = data.roles || [];
  const allKeys = (data.pages || []).map((p) => p.key);
  const sees = (role, key) => {
    if (role.locked) return true;
    const pages = (data.config || {})[role.key];
    return !pages || pages.includes(key);
  };
  const toggle = async (role, key) => {
    const current = (data.config || {})[role.key] || allKeys;
    const next = current.includes(key) ? current.filter((k) => k !== key) : allKeys.filter((k) => k === key || current.includes(k));
    if (!next.length) {
      pushToast({ type: 'error', title: 'A role needs at least one page' });
      return;
    }
    setBusy(`${role.key}:${key}`);
    try {
      setData(await window.AtlasApi.setRolePages(role.key, next.length === allKeys.length ? null : next));
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save', body: e.message });
    } finally { setBusy(null); }
  };
  const grouped = [];
  for (const p of data.pages || []) {
    const g = grouped.find((x) => x.group === p.group);
    if (g) g.pages.push(p); else grouped.push({ group: p.group, pages: [p] });
  }
  const enforced = new Set(data.server_enforced || []);
  const cell = { padding: '6px 8px', textAlign: 'center', borderTop: '1px solid var(--border)' };
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-4)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
        What each role sees
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8, lineHeight: 1.5 }}>
        Unticked pages disappear from that role's sidebar, links and search. A role that keeps only Calling is
        also blocked from CRM data by the API (marked <span style={{ fontSize: 9.5, padding: '0 4px', borderRadius: 3, border: '1px solid var(--border)', color: 'var(--text-3)' }}>API</span>); hiding other pages is display-only.
        {!canEdit && ' Only admins can change this.'}
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-3)' }}>Page</th>
              {roles.map((r) => (
                <th key={r.key} style={{ padding: '8px 8px', fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap' }}
                  title={r.fixed ? 'Deal guests see only the deals they\'re on — set by each deal\'s Deal team, enforced by the API'
                    : r.locked ? 'Admins always see every page' : enforced.has(r.key) ? 'Also enforced by the API' : undefined}>
                  <Pill hue={ROLE_HUE[r.key] || 'slate'} sz="sm">{r.label}</Pill>
                  {enforced.has(r.key) && <span style={{ marginLeft: 4 }}><span style={{ fontSize: 9.5, padding: '0 4px', borderRadius: 3, border: '1px solid var(--border)', color: 'var(--text-3)' }}>API</span></span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map((g) => (
              <React.Fragment key={g.group}>
                <tr>
                  <td colSpan={roles.length + 1} style={{ padding: '8px 12px 3px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)',
                    letterSpacing: '0.05em', textTransform: 'uppercase', borderTop: '1px solid var(--border)' }}>{g.group}</td>
                </tr>
                {g.pages.map((p) => (
                  <tr key={p.key}>
                    <td style={{ padding: '6px 12px', color: 'var(--text)', borderTop: '1px solid var(--border)' }}>{p.label}</td>
                    {roles.map((r) => (
                      <td key={r.key} style={cell}>
                        <input type="checkbox" checked={sees(r, p.key)}
                          disabled={!canEdit || r.locked || r.fixed || busy === `${r.key}:${p.key}`}
                          onChange={() => toggle(r, p.key)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PermissionsSettings = () => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    window.AtlasApi.authPermissions().then(setData).catch((e) => setErr(e.message));
  }, []);

  const groups = React.useMemo(() => {
    const out = new Map();
    for (const p of data?.permissions || []) {
      if (!out.has(p.group)) out.set(p.group, []);
      out.get(p.group).push(p);
    }
    return [...out.entries()];
  }, [data]);

  return (
    <>
      <SettingsHeader
        title="Permissions"
        sub={data ? `Signed in as ${TIER_NAME[data.role] || data.role}` : undefined}
      />
      <div style={{ padding: '20px 28px', maxWidth: 860 }}>
        {data?.has_overrides && (
          <div style={{ fontSize: 11.5, color: 'var(--amber-fg)', marginBottom: 16, lineHeight: 1.5 }}>
            An admin has set per-account exceptions on your profile — the rows marked
            <b> Override</b> below don't follow your role. Ask an admin if you need one changed.
          </div>
        )}
        {err && <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>Couldn't load permissions: {err}</div>}
        {!data && !err && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {data && <RolePagesMatrix canEdit={data.role === 'admin'} />}
        {groups.map(([group, perms]) => (
          <div key={group} style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-4)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>{group}</div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
              {perms.map((p, i) => (
                <div key={p.key} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  borderTop: i ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{p.label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 1 }}>{p.description}</div>
                  </div>
                  <div style={{ width: 90, flexShrink: 0 }}>
                    {p.override === undefined || p.override === null
                      ? <Pill hue={TIER_HUE[p.min_role] || 'slate'} sz="sm">{TIER_NAME[p.min_role] || p.min_role}+</Pill>
                      : <Pill hue={p.override ? 'teal' : 'rose'} sz="sm">Override</Pill>}
                  </div>
                  <div style={{ width: 40, flexShrink: 0, textAlign: 'center' }}
                    title={p.allowed
                      ? (p.override ? 'Granted to your account specifically' : 'You can do this')
                      : (p.override === false ? 'Turned off for your account by an admin' : 'Requires a higher role')}>
                    {p.allowed
                      ? <Icon.Check size={14} color="oklch(0.55 0.13 155)" />
                      : <span style={{ fontSize: 12, color: 'var(--text-4)' }}>—</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

Object.assign(window, {
  AgentsSettings, MembersSettings, MemberPermissionsDrawer, TeamsSettings, IndustriesSettings,
  FieldsSettings, FieldDefinitionDrawer,
  BulkFillJobsSettings, TestRunnerSettings, DataQualitySettings,
  AgentConfigDetail, ConfigField, Limit,
  TagsSettings, DcSyncSettings, CostsSettings, PermissionsSettings,
});
